import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import api from "../../../../shared/api/apiClient";
import ResponsiveSettingsTabs from "../../components/ResponsiveSettingsTabs";
import { crmSettingsApi } from "../../api/crmSettings";
import styles from "./ShopSettingsPage.module.css";


const LEGAL_PAGE_KEYS = ["about", "delivery", "privacy", "terms", "returns", "cookies"];

const LEGAL_TEMPLATE_COPY = {
  ru: {
    about: ["О магазине", "Мы заботимся о качестве товаров, удобстве заказа и понятном сервисе.", "Почему выбирают нас", "Проверенный ассортимент", "Удобное оформление заказа", "Поддержка покупателей"],
    delivery: ["Доставка и получение", "Выберите удобный способ получения заказа.", "Варианты получения", "Самовывоз — после подтверждения готовности", "Курьерская доставка — по доступному адресу", "Посылочный автомат — если доступен при оформлении", "Важно", "Стоимость и срок отображаются или подтверждаются при оформлении заказа."],
    privacy: ["Политика конфиденциальности", "Мы обрабатываем персональные данные только в объёме, необходимом для работы магазина и выполнения заказа.", "Какие данные используются", "Контактные и платёжные данные, сведения о заказе и технические данные, необходимые для работы сайта.", "Ваши права", "Вы можете запросить доступ, исправление или удаление данных в пределах, предусмотренных законом."],
    terms: ["Условия покупки", "Оформляя заказ, покупатель подтверждает корректность введённых данных и согласие с условиями магазина.", "Оформление заказа", "Цена, наличие, доставка и итоговая сумма отображаются до подтверждения заказа.", "Оплата", "Доступные способы оплаты показываются при оформлении заказа."],
    returns: ["Возврат и гарантия", "Правила возврата и гарантии применяются в соответствии с условиями магазина и действующим законодательством.", "Как оформить возврат", "Свяжитесь с нами, укажите номер заказа и причину обращения.", "Состояние товара", "Сохраните товар, комплектность и подтверждение покупки до завершения рассмотрения."],
    cookies: ["Политика cookie", "Cookie помогают сайту работать корректно, сохранять настройки и улучшать пользовательский опыт.", "Необходимые cookie", "Используются для базовой работы магазина и не отключаются.", "Дополнительные cookie", "Аналитические и маркетинговые cookie используются только согласно выбранным настройкам согласия."],
  },
  et: {
    about: ["Poest", "Hoolime toodete kvaliteedist, mugavast tellimisest ja selgest teenindusest.", "Miks meid valida", "Kontrollitud valik", "Mugav tellimine", "Klienditugi"],
    delivery: ["Tarne ja kättesaamine", "Valige endale sobiv tellimuse kättesaamise viis.", "Kättesaamise viisid", "Ise järele — pärast valmisoleku kinnitust", "Kuller — saadaval olevale aadressile", "Pakiautomaat — kui see on kassas saadaval", "Oluline", "Hind ja tarneaeg kuvatakse või kinnitatakse tellimuse vormistamisel."],
    privacy: ["Privaatsuspoliitika", "Töötleme isikuandmeid ainult e-poe toimimiseks ja tellimuse täitmiseks vajalikus ulatuses.", "Milliseid andmeid kasutame", "Kontakt- ja makseandmed, tellimuse info ning veebilehe tööks vajalikud tehnilised andmed.", "Teie õigused", "Teil on seaduses sätestatud ulatuses õigus taotleda andmetele juurdepääsu, parandamist või kustutamist."],
    terms: ["Ostutingimused", "Tellimuse esitamisega kinnitab ostja sisestatud andmete õigsust ja nõustumist poe tingimustega.", "Tellimuse vormistamine", "Hind, saadavus, tarne ja lõppsumma kuvatakse enne tellimuse kinnitamist.", "Maksmine", "Saadaolevad makseviisid kuvatakse kassas."],
    returns: ["Tagastus ja garantii", "Tagastusele ja garantiile kehtivad poe tingimused ning kohaldatav seadus.", "Kuidas tagastada", "Võtke meiega ühendust ning märkige tellimuse number ja pöördumise põhjus.", "Kauba seisukord", "Hoidke kaup, komplektsus ja ostutõend alles kuni menetluse lõppemiseni."],
    cookies: ["Küpsiste poliitika", "Küpsised aitavad veebilehel korrektselt töötada, seadeid meeles pidada ja kasutuskogemust parandada.", "Vajalikud küpsised", "Neid kasutatakse poe põhifunktsioonide jaoks ja neid ei saa välja lülitada.", "Täiendavad küpsised", "Analüütilisi ja turundusküpsiseid kasutatakse vastavalt teie nõusoleku valikutele."],
  },
  en: {
    about: ["About us", "We focus on product quality, convenient ordering and clear customer service.", "Why choose us", "Curated assortment", "Easy ordering", "Customer support"],
    delivery: ["Delivery and collection", "Choose the most convenient way to receive your order.", "Delivery options", "Pickup — after your order is confirmed ready", "Courier — to an available delivery address", "Parcel locker — when available at checkout", "Important", "The price and estimated delivery time are shown or confirmed during checkout."],
    privacy: ["Privacy policy", "We process personal data only to the extent required to operate the store and fulfil orders.", "Data we use", "Contact and payment details, order information and technical data required for the website to function.", "Your rights", "You may request access, correction or deletion of your data where provided by law."],
    terms: ["Terms of purchase", "By placing an order, the customer confirms that the entered information is correct and accepts the store terms.", "Placing an order", "Price, availability, delivery and the final amount are shown before the order is confirmed.", "Payment", "Available payment methods are shown during checkout."],
    returns: ["Returns and warranty", "Returns and warranty are handled according to the store terms and applicable law.", "How to request a return", "Contact us and provide the order number and reason for the request.", "Product condition", "Keep the product, accessories and proof of purchase until the request is resolved."],
    cookies: ["Cookie policy", "Cookies help the website function correctly, remember settings and improve the shopping experience.", "Necessary cookies", "Used for essential store functionality and cannot be disabled.", "Optional cookies", "Analytics and marketing cookies are used according to your consent choices."],
  },
};

function escapeHtml(value) {
  return String(value || "").replace(/[&<>\"]/g, (char) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[char]));
}

function buildLegalTemplate(page, language) {
  const c = LEGAL_TEMPLATE_COPY[language]?.[page] || LEGAL_TEMPLATE_COPY.ru[page];
  if (!c) return "";
  const [title, intro, sectionOne, itemOne, itemTwo, itemThree, sectionTwo, finalText] = c;
  if (page === "about") {
    return `<section class="shop-info-template"><div class="shop-info-lead"><h2>${escapeHtml(title)}</h2><p>${escapeHtml(intro)}</p></div><div class="shop-info-feature-grid"><article><strong>✓</strong><span>${escapeHtml(itemOne)}</span></article><article><strong>✓</strong><span>${escapeHtml(itemTwo)}</span></article><article><strong>✓</strong><span>${escapeHtml(itemThree)}</span></article></div></section>`;
  }
  return `<section class="shop-info-template"><div class="shop-info-lead"><h2>${escapeHtml(title)}</h2><p>${escapeHtml(intro)}</p></div><div class="shop-info-panel"><h3>${escapeHtml(sectionOne)}</h3><ul><li>${escapeHtml(itemOne)}</li>${itemTwo ? `<li>${escapeHtml(itemTwo)}</li>` : ""}${itemThree ? `<li>${escapeHtml(itemThree)}</li>` : ""}</ul></div>${sectionTwo ? `<div class="shop-info-note"><strong>${escapeHtml(sectionTwo)}</strong><p>${escapeHtml(finalText)}</p></div>` : ""}</section>`;
}


function sanitizeInlineStyle(value) {
  const allowed = new Set(["color", "background", "background-color", "border", "border-color", "border-radius", "padding", "padding-top", "padding-right", "padding-bottom", "padding-left", "margin", "margin-top", "margin-right", "margin-bottom", "margin-left", "text-align", "font-size", "font-weight", "line-height", "letter-spacing", "display", "grid-template-columns", "gap", "align-items", "justify-content", "max-width", "width"]);
  return String(value || "").split(";").map((part) => part.trim()).filter(Boolean).map((part) => {
    const colon = part.indexOf(":");
    if (colon < 1) return "";
    const prop = part.slice(0, colon).trim().toLowerCase();
    const val = part.slice(colon + 1).trim();
    if (!allowed.has(prop) || /url\s*\(|expression\s*\(|javascript:/i.test(val)) return "";
    return `${prop}:${val}`;
  }).filter(Boolean).join(";");
}

function sanitizeLegalHtml(html) {
  if (typeof window === "undefined" || typeof DOMParser === "undefined") return escapeHtml(html);
  const allowedTags = new Set(["section", "div", "article", "p", "br", "strong", "b", "em", "i", "u", "h2", "h3", "h4", "ul", "ol", "li", "a", "span", "small", "hr", "blockquote"]);
  const allowedClasses = new Set(["shop-info-template", "shop-info-lead", "shop-info-feature-grid", "shop-info-panel", "shop-info-note"]);
  const doc = new DOMParser().parseFromString(`<div id="sanitize-root">${String(html || "")}</div>`, "text/html");
  const root = doc.getElementById("sanitize-root");
  Array.from(root.querySelectorAll("*")).forEach((node) => {
    const tag = node.tagName.toLowerCase();
    if (!allowedTags.has(tag)) { node.replaceWith(...Array.from(node.childNodes)); return; }
    Array.from(node.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase();
      if (name === "style") {
        const safeStyle = sanitizeInlineStyle(attr.value);
        if (safeStyle) node.setAttribute("style", safeStyle); else node.removeAttribute("style");
        return;
      }
      if (name === "class") {
        const safe = attr.value.split(/\s+/).filter((value) => allowedClasses.has(value));
        if (safe.length) node.setAttribute("class", safe.join(" ")); else node.removeAttribute("class");
        return;
      }
      if (tag === "a" && name === "href") {
        const href = String(attr.value || "").trim();
        if (/^(https?:|mailto:|tel:|#|\/)/i.test(href)) node.setAttribute("href", href); else node.removeAttribute("href");
        return;
      }
      if (tag === "a" && ["target", "rel"].includes(name)) return;
      node.removeAttribute(attr.name);
    });
    if (tag === "a" && node.getAttribute("target") === "_blank") node.setAttribute("rel", "noopener noreferrer");
  });
  return root.innerHTML;
}

function htmlTextSegments(html) {
  if (typeof window === "undefined" || typeof DOMParser === "undefined") return { texts: [], apply: () => html };
  const doc = new DOMParser().parseFromString(`<div id="legal-root">${String(html || "")}</div>`, "text/html");
  const root = doc.getElementById("legal-root");
  const nodes = [];
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node;
  while ((node = walker.nextNode())) {
    const parentTag = String(node.parentElement?.tagName || "").toLowerCase();
    if (["script", "style", "code", "pre"].includes(parentTag)) continue;
    if (String(node.nodeValue || "").trim()) nodes.push(node);
  }
  return {
    texts: nodes.map((item) => String(item.nodeValue || "").trim()),
    apply(translatedTexts) {
      nodes.forEach((item, index) => {
        const original = String(item.nodeValue || "");
        const leading = original.match(/^\s*/)?.[0] || "";
        const trailing = original.match(/\s*$/)?.[0] || "";
        item.nodeValue = `${leading}${translatedTexts[index] ?? original.trim()}${trailing}`;
      });
      return root.innerHTML;
    },
  };
}

const SUPPORTED_SHOP_CURRENCIES = [
  { code: "USD", label: "$ USD" },
  { code: "EUR", label: "€ EUR" },
  { code: "GBP", label: "£ GBP" },
];

function normalizeShopCurrency(value) {
  const code = String(value || "").trim().toUpperCase();
  return SUPPORTED_SHOP_CURRENCIES.some((item) => item.code === code) ? code : "EUR";
}

function shopCurrencySymbol(value) {
  const code = normalizeShopCurrency(value);
  return SUPPORTED_SHOP_CURRENCIES.find((item) => item.code === code)?.label.split(" ")[0] || "€";
}

const LIGHT_THEME_DEFAULTS = {
  primary_color: "#5d5fef",
  page_background: "#fbfafc",
  surface_color: "#ffffff",
  header_background: "#fbfafc",
  text_color: "#171721",
  muted_color: "#6e6e7b",
  border_color: "#e7e5ec",
};

const THEME_BACKGROUND_FIELDS = new Set(["page_background", "surface_color", "header_background"]);

function hexToRgb(hex) {
  const value = String(hex || "").replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(value)) return null;
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
}

function rgbToHex({ r, g, b }) {
  return `#${[r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0")).join("")}`;
}

function mixHex(foreground, background, amount) {
  const a = hexToRgb(foreground);
  const b = hexToRgb(background);
  if (!a || !b) return foreground;
  return rgbToHex({
    r: a.r * (1 - amount) + b.r * amount,
    g: a.g * (1 - amount) + b.g * amount,
    b: a.b * (1 - amount) + b.b * amount,
  });
}

function relativeLuminance(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return 1;
  const channel = (value) => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
}

function contrastRatio(a, b) {
  const l1 = relativeLuminance(a);
  const l2 = relativeLuminance(b);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

function autoThemeColors(theme) {
  const backgrounds = [theme.page_background, theme.surface_color, theme.header_background].filter(Boolean);
  const candidates = ["#151722", "#ffffff"];
  const text = candidates
    .map((color) => ({ color, score: Math.min(...backgrounds.map((bg) => contrastRatio(color, bg))) }))
    .sort((a, b) => b.score - a.score)[0]?.color || "#151722";
  const anchor = theme.surface_color || theme.page_background || "#ffffff";
  return {
    text_color: text,
    muted_color: mixHex(text, anchor, text === "#ffffff" ? 0.34 : 0.42),
    border_color: mixHex(text, anchor, text === "#ffffff" ? 0.78 : 0.86),
  };
}


const EMPTY_FORM = {
  enabled: true,
  sales_mode: "shop",
  commerce_modules: { shop: true, auction: false, marketplace: false },
  marketplace: {
    require_moderation: true, default_listing_days: 30, max_images: 8, allow_phone: true,
    user_listings: {
      enabled: false, free_per_week: 3, max_active: 10, max_photos: 8, lifetime_days: 30,
      moderation_required: true, remoderate_after_edit: true, allow_phone: true, internal_chat: true,
      city_required: true, price_required: true, negotiable_price: true, allow_services: true,
    },
  },
  auction: {
    show_bid_count: true,
    show_bid_history: true,
    anti_sniping_enabled: true,
    anti_sniping_minutes: 2,
    default_increment: 1,
  },
  public_url: "",
  store_name: "",
  tagline: "",
  currency: "EUR",
  locale: "ru",
  use_crm_assets: true,
  logo_key: "",
  logo_url: "",
  favicon_key: "",
  favicon_url: "",
  ...LIGHT_THEME_DEFAULTS,
  auto_text_contrast: true,
  body_font_scale: 100,
  heading_font_scale: 100,
  support_email: "",
  support_phone: "",
  address: "",
  contact_details: {
    legal_name: "",
    registry_code: "",
    vat_number: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    postal_code: "",
    working_hours: { ru: "", en: "" },
    extra_fields: [],
  },
  service_locations: [],
  checkout_config: {
    delivery: {
      pickup: { enabled: true, price: 0 },
      courier: { enabled: true, price: 5, free_from: 0 },
      parcel: { enabled: true, price: 3, free_from: 0 },
    },
    payment: { bank_transfer: true, pay_on_pickup: true, montonio: { enabled: false, environment: "sandbox", country: "EE", access_key: "", secret_key: "" } },
  },
  publish_products_by_default: true,
  about_text: "",
  delivery_text: "",
  privacy_text: "",
  terms_text: "",
  legal: {
    about: { ru: "", et: "", en: "" },
    delivery: { ru: "", et: "", en: "" },
    privacy: { ru: "", et: "", en: "" },
    terms: { ru: "", et: "", en: "" },
    returns: { ru: "", et: "", en: "" },
    cookies: { ru: "", et: "", en: "" },
  },
  cookie_settings: {
    enabled: true,
    title: "",
    text: "",
    analytics_enabled: true,
    marketing_enabled: true,
  },
  homepage: {
    enabled: true,
    template: "modern",
    catalog_menu: { enabled: true, mode: "auto", category_ids: [] },
    slides: [],
    sections: [
      { id: "categories", type: "category_cards", enabled: true, title: { ru: "Популярные категории", en: "Popular categories" } },
      { id: "featured", type: "product_carousel", enabled: true, title: { ru: "Рекомендуем", en: "Featured products" }, source: "featured" },
      { id: "promo", type: "promo_banner", enabled: false, title: { ru: "", en: "" }, text: { ru: "", en: "" }, button: { ru: "", en: "" }, url: "" },
    ],
  },
  crm_workflow: {
    enabled: true,
    lead_source_name: "VelShop",
    owner_id: "",
    task_due_minutes: 60,
    task_title: "",
  },
};

const SETTINGS_GROUP_TABS = {
  common: ["general", "branding", "contacts", "checkout", "content", "support", "workflow"],
  shop: ["homepage", "reviews"],
  auction: ["auction"],
  marketplace: ["marketplace"],
};
const asText = (value, fallback = "") =>
  value === null || value === undefined ? fallback : String(value);

function normalize(settings) {
  const storefront =
    settings?.settings_json?.storefront &&
    typeof settings.settings_json.storefront === "object"
      ? settings.settings_json.storefront
      : {};
  const workflow =
    storefront.crm_workflow && typeof storefront.crm_workflow === "object"
      ? storefront.crm_workflow
      : {};

  return {
    form: {
      ...EMPTY_FORM,
      enabled: storefront.enabled !== false,
      sales_mode: ["shop", "auction", "hybrid"].includes(String(storefront.sales_mode || "shop")) ? String(storefront.sales_mode || "shop") : "shop",
      commerce_modules: (() => {
        const raw = storefront.commerce_modules && typeof storefront.commerce_modules === "object" ? storefront.commerce_modules : null;
        if (raw) return { shop: raw.shop !== false, auction: Boolean(raw.auction), marketplace: Boolean(raw.marketplace) };
        const mode = String(storefront.sales_mode || "shop");
        return { shop: mode !== "auction", auction: mode === "auction" || mode === "hybrid", marketplace: false };
      })(),
      marketplace: (() => {
        const raw = storefront.marketplace && typeof storefront.marketplace === "object" ? storefront.marketplace : {};
        return {
          ...EMPTY_FORM.marketplace,
          ...raw,
          user_listings: {
            ...EMPTY_FORM.marketplace.user_listings,
            ...(raw.user_listings && typeof raw.user_listings === "object" ? raw.user_listings : {}),
          },
        };
      })(),
      auction: {
        ...EMPTY_FORM.auction,
        ...(storefront.auction && typeof storefront.auction === "object" ? storefront.auction : {}),
      },
      public_url: asText(storefront.public_url),
      store_name: asText(storefront.store_name, settings?.company_name || ""),
      tagline: asText(storefront.tagline),
      currency: normalizeShopCurrency(asText(storefront.currency, settings?.currency || "EUR")),
      locale: asText(storefront.locale, "ru"),
      use_crm_assets: storefront.use_crm_assets !== false,
      logo_key: asText(storefront.logo_key),
      logo_url: asText(storefront.logo_url),
      favicon_key: asText(storefront.favicon_key),
      favicon_url: asText(storefront.favicon_url),
      primary_color: asText(
        storefront.primary_color,
        settings?.primary_color || LIGHT_THEME_DEFAULTS.primary_color
      ),
      page_background: asText(
        storefront.page_background,
        LIGHT_THEME_DEFAULTS.page_background
      ),
      surface_color: asText(storefront.surface_color, LIGHT_THEME_DEFAULTS.surface_color),
      header_background: asText(
        storefront.header_background,
        LIGHT_THEME_DEFAULTS.header_background
      ),
      text_color: asText(storefront.text_color, LIGHT_THEME_DEFAULTS.text_color),
      muted_color: asText(storefront.muted_color, LIGHT_THEME_DEFAULTS.muted_color),
      border_color: asText(storefront.border_color, LIGHT_THEME_DEFAULTS.border_color),
      auto_text_contrast: storefront.auto_text_contrast !== false,
      body_font_scale: Math.max(85, Math.min(120, Number(storefront.body_font_scale || 100))),
      heading_font_scale: Math.max(90, Math.min(130, Number(storefront.heading_font_scale || 100))),
      support_email: asText(storefront.support_email),
      support_phone: asText(storefront.support_phone),
      address: asText(storefront.address),
      contact_details: normalizeContactDetails(storefront.contact_details, storefront),
      service_locations: normalizeServiceLocations(storefront.service_locations),
      checkout_config: normalizeCheckoutConfig(storefront.checkout_config),
      publish_products_by_default: storefront.publish_products_by_default !== false,
      about_text: asText(storefront.about_text),
      delivery_text: asText(storefront.delivery_text),
      privacy_text: asText(storefront.privacy_text),
      terms_text: asText(storefront.terms_text),
      legal: normalizeLegal(storefront.legal, storefront),
      cookie_settings: normalizeCookieSettings(storefront.cookie_settings),
      homepage: normalizeHomepage(storefront.homepage),
      crm_workflow: {
        ...EMPTY_FORM.crm_workflow,
        ...workflow,
        enabled: true,
        owner_id:
          workflow.owner_id === null || workflow.owner_id === undefined
            ? ""
            : String(workflow.owner_id),
        task_due_minutes: Number.isFinite(Number(workflow.task_due_minutes))
          ? Number(workflow.task_due_minutes)
          : 60,
        lead_source_name: asText(workflow.lead_source_name, "VelShop"),
        task_title: asText(workflow.task_title),
      },
    },
    crmAssets: {
      logo_url: asText(settings?.logo_url),
      favicon_url: asText(settings?.favicon_url),
      company_name: asText(settings?.company_name),
    },
  };
}

function storagePreviewUrl(attachmentId) {
  if (!attachmentId) return "";
  const base = String(api?.defaults?.baseURL || "").replace(/\/$/, "");
  return `${base}/auth/storage/files/${attachmentId}/download`;
}

function localized(value) {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return { ru: asText(value.ru), et: asText(value.et), en: asText(value.en) };
  }
  return { ru: asText(value), et: "", en: asText(value) };
}

function normalizeContactDetails(value, storefront = {}) {
  const source = value && typeof value === "object" ? value : {};
  const extra = Array.isArray(source.extra_fields) ? source.extra_fields : [];
  return {
    legal_name: asText(source.legal_name),
    registry_code: asText(source.registry_code),
    vat_number: asText(source.vat_number),
    email: asText(source.email, storefront.support_email || ""),
    phone: asText(source.phone, storefront.support_phone || ""),
    address: asText(source.address, storefront.address || ""),
    city: asText(source.city),
    postal_code: asText(source.postal_code),
    working_hours: localized(source.working_hours),
    extra_fields: extra.map((item, index) => ({
      id: asText(item?.id, `field-${index + 1}`),
      label: localized(item?.label),
      value: localized(item?.value),
    })),
  };
}

function normalizeServiceLocations(value) {
  const items = Array.isArray(value) ? value : [];
  return items.map((item, index) => ({
    id: asText(item?.id, `location-${index + 1}`),
    name: localized(item?.name),
    city: localized(item?.city),
    address: localized(item?.address),
    phone: asText(item?.phone),
    email: asText(item?.email),
    working_hours: localized(item?.working_hours),
    latitude: asText(item?.latitude),
    longitude: asText(item?.longitude),
    extra_fields: (Array.isArray(item?.extra_fields) ? item.extra_fields : []).map((field, fieldIndex) => ({
      id: asText(field?.id, `field-${fieldIndex + 1}`),
      label: localized(field?.label),
      value: localized(field?.value),
    })),
  }));
}



function normalizeCheckoutConfig(value) {
  const source = value && typeof value === "object" ? value : {};
  const delivery = source.delivery && typeof source.delivery === "object" ? source.delivery : {};
  const payment = source.payment && typeof source.payment === "object" ? source.payment : {};
  const method = (key, defaults) => {
    const item = delivery[key] && typeof delivery[key] === "object" ? delivery[key] : {};
    return {
      enabled: item.enabled !== false,
      price: Math.max(0, Number(item.price ?? defaults.price) || 0),
      free_from: Math.max(0, Number(item.free_from ?? defaults.free_from) || 0),
    };
  };
  return {
    delivery: {
      pickup: method("pickup", { price: 0, free_from: 0 }),
      courier: method("courier", { price: 5, free_from: 0 }),
      parcel: method("parcel", { price: 3, free_from: 0 }),
    },
    payment: {
      bank_transfer: payment.bank_transfer !== false,
      pay_on_pickup: payment.pay_on_pickup !== false,
      montonio: {
        enabled: payment.montonio?.enabled === true,
        environment: payment.montonio?.environment === "production" ? "production" : "sandbox",
        country: ["EE", "LV", "LT", "FI", "PL", "DE"].includes(String(payment.montonio?.country || "EE").toUpperCase()) ? String(payment.montonio?.country || "EE").toUpperCase() : "EE",
        access_key: asText(payment.montonio?.access_key),
        secret_key: asText(payment.montonio?.secret_key),
      },
    },
  };
}

function normalizeLegal(value, storefront = {}) {
  const source = value && typeof value === "object" ? value : {};
  const legacy = (key) => asText(storefront?.[`${key}_text`]);
  return {
    about: localized(source.about || legacy("about")),
    delivery: localized(source.delivery || legacy("delivery")),
    privacy: localized(source.privacy || legacy("privacy")),
    terms: localized(source.terms || legacy("terms")),
    returns: localized(source.returns || ""),
    cookies: localized(source.cookies || ""),
  };
}

function normalizeCookieSettings(value) {
  const source = value && typeof value === "object" ? value : {};
  return {
    enabled: source.enabled !== false,
    title: asText(source.title),
    text: asText(source.text),
    analytics_enabled: source.analytics_enabled !== false,
    marketing_enabled: source.marketing_enabled !== false,
  };
}


function normalizeHomepage(value) {
  const source = value && typeof value === "object" ? value : {};
  const catalog = source.catalog_menu && typeof source.catalog_menu === "object" ? source.catalog_menu : {};
  const slides = Array.isArray(source.slides) ? source.slides : [];
  const sections = Array.isArray(source.sections) && source.sections.length
    ? source.sections
    : EMPTY_FORM.homepage.sections;
  return {
    enabled: source.enabled !== false,
    template: source.template === "marketplace" ? "marketplace" : "modern",
    catalog_menu: {
      enabled: catalog.enabled !== false,
      mode: catalog.mode === "manual" ? "manual" : "auto",
      category_ids: Array.isArray(catalog.category_ids) ? catalog.category_ids.map(Number).filter(Boolean) : [],
    },
    category_images: Object.fromEntries(
      Object.entries(source.category_images && typeof source.category_images === "object" ? source.category_images : {})
        .map(([categoryId, value]) => {
          const attachmentId = Number(value?.attachment_id || value || 0) || null;
          return [String(categoryId), {
            attachment_id: attachmentId,
            preview_url: asText(value?.preview_url, attachmentId ? storagePreviewUrl(attachmentId) : ""),
          }];
        })
        .filter(([, value]) => value.attachment_id)
    ),
    slides: slides.map((item, index) => ({
      id: asText(item?.id, `slide-${index + 1}`),
      enabled: item?.enabled !== false,
      attachment_id: Number(item?.attachment_id || 0) || null,
      mobile_attachment_id: Number(item?.mobile_attachment_id || 0) || null,
      preview_url: asText(item?.preview_url, item?.attachment_id ? storagePreviewUrl(item.attachment_id) : ""),
      mobile_preview_url: asText(item?.mobile_preview_url, item?.mobile_attachment_id ? storagePreviewUrl(item.mobile_attachment_id) : ""),
      title: localized(item?.title), text: localized(item?.text), button: localized(item?.button),
      link_type: asText(item?.link_type, "none"), target_id: Number(item?.target_id || 0) || null, url: asText(item?.url),
    })),
    sections: sections.map((item, index) => ({
      id: asText(item?.id, `section-${index + 1}`),
      type: asText(item?.type, "category_cards"),
      enabled: item?.enabled !== false,
      title: localized(item?.title), text: localized(item?.text), button: localized(item?.button),
      source: asText(item?.source, "featured"), url: asText(item?.url),
      limit: Math.min(12, Math.max(1, Number(item?.limit || 8))),
      layout: asText(item?.layout, "default"),
      video_url: asText(item?.video_url),
      items: Array.isArray(item?.items) ? item.items.map((entry, itemIndex) => ({
        id: asText(entry?.id, `item-${itemIndex + 1}`), title: localized(entry?.title), text: localized(entry?.text), icon: asText(entry?.icon, "sparkles"),
      })) : [],
    })),
  };
}

function cleanLocalized(value) {
  return {
    ru: asText(value?.ru).trim(),
    et: asText(value?.et).trim(),
    en: asText(value?.en).trim(),
  };
}

function createId(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function isHttpUrl(value) {
  if (!value) return true;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

export default function ShopSettingsPage() {
  const { t, i18n } = useTranslation();
  const shopText = (ru, en) => String(i18n?.resolvedLanguage || i18n?.language || "en").toLowerCase().startsWith("ru") ? ru : en;
  const [activeSection, setActiveSection] = useState("common");
  const [activeTab, setActiveTab] = useState("general");
  const [form, setForm] = useState(EMPTY_FORM);
  const [baseline, setBaseline] = useState(EMPTY_FORM);
  const [crmAssets, setCrmAssets] = useState({ logo_url: "", favicon_url: "" });
  const [owners, setOwners] = useState([]);
  const [shopCategories, setShopCategories] = useState([]);
  const [shopCatalogTree, setShopCatalogTree] = useState([]);
  const [homepageBusy, setHomepageBusy] = useState("");
  const [homepageUploadProgress, setHomepageUploadProgress] = useState({});
  const [supportThreads, setSupportThreads] = useState([]);
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportError, setSupportError] = useState("");
  const [supportReply, setSupportReply] = useState({});
  const [supportBusy, setSupportBusy] = useState("");
  const [shopReviews, setShopReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState("");
  const [reviewsFilter, setReviewsFilter] = useState("planned");
  const [reviewBusy, setReviewBusy] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assetBusy, setAssetBusy] = useState("");
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [legalLanguage, setLegalLanguage] = useState("ru");
  const [legalTranslationBusy, setLegalTranslationBusy] = useState("");
  const [legalTranslationProgress, setLegalTranslationProgress] = useState(0);
  const mountedRef = useRef(true);

  const sectionTabs = useMemo(() => {
    const base = [...(SETTINGS_GROUP_TABS[activeSection] || SETTINGS_GROUP_TABS.common)];
    if (activeSection === "marketplace" && form.marketplace?.user_listings?.enabled) base.push("user_listings");
    return base;
  }, [activeSection, form.marketplace?.user_listings?.enabled]);

  const tabs = useMemo(
    () => sectionTabs.map((key) => ({
      key,
      label: key === "reviews" ? t("shop_ui_reviews")
        : key === "checkout" ? t("shop_ui_delivery_payment")
        : key === "auction" ? t("shop_ui_auction")
        : key === "marketplace" ? t("shop_ui_owner_listings")
        : key === "user_listings" ? t("shop_ui_customer_listings")
        : t(`shop_settings_${key}`),
    })),
    [sectionTabs, t, i18n.language]
  );

  const settingsSections = useMemo(() => {
    const result = [{ key: "common", label: t("shop_ui_common_settings"), hint: t("shop_ui_branding_contacts_delivery_and_shared_retailshop_rules") }];
    if (form.commerce_modules?.shop) result.push({ key: "shop", label: t("shop_ui_online_store"), hint: t("shop_ui_homepage_catalog_and_reviews") });
    if (form.commerce_modules?.auction) result.push({ key: "auction", label: t("shop_ui_auction"), hint: t("shop_ui_lots_bids_and_auction_rules") });
    if (form.commerce_modules?.marketplace) result.push({ key: "marketplace", label: t("shop_ui_marketplace"), hint: t("shop_ui_business_and_customer_listings") });
    return result;
  }, [form.commerce_modules, i18n.language]);


  useEffect(() => {
    const allowedSections = settingsSections.map((item) => item.key);
    if (!allowedSections.includes(activeSection)) { setActiveSection("common"); setActiveTab("general"); return; }
    if (!sectionTabs.includes(activeTab)) setActiveTab(sectionTabs[0] || "general");
  }, [settingsSections, sectionTabs, activeSection, activeTab]);

  const isDirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(baseline),
    [baseline, form]
  );

  useEffect(() => {
    mountedRef.current = true;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const settings = await crmSettingsApi.get();
        if (!mountedRef.current) return;
        const next = normalize(settings);
        setForm(next.form);
        setBaseline(next.form);
        setCrmAssets(next.crmAssets);
      } catch (requestError) {
        if (mountedRef.current) {
          setError(
            requestError?.response?.data?.detail || t("shop_settings_load_error")
          );
        }
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    };

    load();
    api
      .get("/auth/users/owners", { params: { limit: 200 } })
      .then(({ data }) => {
        if (mountedRef.current) setOwners(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (mountedRef.current) setOwners([]);
      });
    api
      .get("/auth/category/all", { params: { limit: 500 } })
      .then(({ data }) => {
        if (mountedRef.current) setShopCategories(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (mountedRef.current) setShopCategories([]);
      });
    api
      .get("/auth/catalog/tree")
      .then(({ data }) => {
        if (mountedRef.current) setShopCatalogTree(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (mountedRef.current) setShopCatalogTree([]);
      });

    return () => {
      mountedRef.current = false;
    };
  }, [t]);

  useEffect(() => {
    if (activeTab !== "support") return undefined;
    let active = true;
    setSupportLoading(true);
    setSupportError("");
    api.get("/auth/crmSettings/storefront/support", { params: { limit: 100 } })
      .then(({ data }) => { if (active) setSupportThreads(Array.isArray(data?.items) ? data.items : []); })
      .catch((requestError) => { if (active) setSupportError(requestError?.response?.data?.detail || t("shop_support_load_error")); })
      .finally(() => { if (active) setSupportLoading(false); });
    return () => { active = false; };
  }, [activeTab, t]);

  useEffect(() => {
    if (activeTab !== "reviews") return undefined;
    let active = true;
    setReviewsLoading(true);
    setReviewsError("");
    api.get("/auth/shop-reviews", { params: { review_status: reviewsFilter, limit: 300 } })
      .then(({ data }) => { if (active) setShopReviews(Array.isArray(data?.items) ? data.items : []); })
      .catch((requestError) => { if (active) setReviewsError(requestError?.response?.data?.detail || t("shop_ui_could_not_load_reviews")); })
      .finally(() => { if (active) setReviewsLoading(false); });
    return () => { active = false; };
  }, [activeTab, reviewsFilter, t]);

  const moderateReview = async (id, status) => {
    if (reviewBusy) return;
    setReviewBusy(id);
    setReviewsError("");
    try {
      await api.put(`/auth/shop-reviews/${id}`, { status });
      setShopReviews((current) => current.filter((row) => row.id !== id));
    } catch (requestError) {
      setReviewsError(requestError?.response?.data?.detail || t("shop_ui_could_not_update_review"));
    } finally {
      setReviewBusy(0);
    }
  };

  const sendSupportReply = async (threadId) => {
    const body = String(supportReply[threadId] || "").trim();
    if (!body || supportBusy) return;
    setSupportBusy(threadId);
    setSupportError("");
    try {
      const { data } = await api.post(`/auth/crmSettings/storefront/support/${encodeURIComponent(threadId)}/reply`, { body });
      setSupportThreads((current) => current.map((thread) => thread.thread_id === threadId
        ? { ...thread, updated_at: data.created_at, messages: [...(thread.messages || []), data] }
        : thread));
      setSupportReply((current) => ({ ...current, [threadId]: "" }));
    } catch (requestError) {
      setSupportError(requestError?.response?.data?.detail || t("shop_support_reply_error"));
    } finally {
      setSupportBusy("");
    }
  };

  const setLegalField = (page, language, value) => {
    setSaved(false);
    setForm((current) => ({ ...current, legal: { ...current.legal, [page]: { ...current.legal[page], [language]: value } } }));
  };

  const legalLanguages = [
    { code: "ru", short: "RU", label: "Русский" },
    { code: "et", short: "ET", label: "Eesti" },
    { code: "en", short: "EN", label: "English" },
  ];

  const translateLegalPages = async (targetLanguage) => {
    const sourceLanguage = legalLanguage;
    if (!targetLanguage || targetLanguage === sourceLanguage || legalTranslationBusy) return;

    const sourceDocs = {};
    const texts = {};
    LEGAL_PAGE_KEYS.forEach((page) => {
      const html = String(form.legal?.[page]?.[sourceLanguage] || "").trim();
      const parsed = htmlTextSegments(html);
      sourceDocs[page] = parsed;
      parsed.texts.forEach((text, index) => {
        texts[`${page}__${index}`] = text;
      });
    });

    if (!Object.values(texts).some(Boolean)) {
      setError(t("shop_translate_source_empty", { defaultValue: "Сначала заполните текст на текущем языке" }));
      return;
    }

    const targetHasContent = LEGAL_PAGE_KEYS.some((page) => String(form.legal?.[page]?.[targetLanguage] || "").trim());
    if (targetHasContent) {
      const targetLabel = legalLanguages.find((item) => item.code === targetLanguage)?.short || targetLanguage.toUpperCase();
      const confirmed = window.confirm(
        t("shop_translate_overwrite_confirm", {
          defaultValue: `В ${targetLabel} уже есть текст. Заменить его автоматическим переводом?`,
        })
      );
      if (!confirmed) return;
    }

    try {
      setError("");
      setLegalTranslationBusy(targetLanguage);
      setLegalTranslationProgress(0);

      const startResponse = await api.post("/auth/shop/translate/job", {
        source_lang: sourceLanguage,
        target_lang: targetLanguage,
        texts,
      });
      const jobId = startResponse.data?.job_id;
      if (!jobId) throw new Error(t("shop_translate_error", { defaultValue: "Не удалось запустить перевод" }));

      let completed = null;
      for (let attempt = 0; attempt < 240; attempt += 1) {
        const statusResponse = await api.get(`/auth/shop/translate/job/${jobId}`);
        const status = statusResponse.data || {};
        setLegalTranslationProgress(Math.max(0, Math.min(100, Number(status.progress || 0))));
        if (status.status === "completed") { completed = status; break; }
        if (status.status === "failed") throw new Error(status.error || t("shop_translate_error", { defaultValue: "Не удалось выполнить автоматический перевод" }));
        await new Promise((resolve) => window.setTimeout(resolve, 650));
      }
      if (!completed) throw new Error(t("shop_translate_error", { defaultValue: "Перевод занял слишком много времени. Попробуйте ещё раз." }));

      const translated = completed.translations || {};
      setForm((current) => {
        const nextLegal = { ...current.legal };
        LEGAL_PAGE_KEYS.forEach((page) => {
          const translatedNodes = sourceDocs[page].texts.map((_, index) => String(translated[`${page}__${index}`] || sourceDocs[page].texts[index] || ""));
          nextLegal[page] = { ...nextLegal[page], [targetLanguage]: sourceDocs[page].apply(translatedNodes) };
        });
        return { ...current, legal: nextLegal };
      });
      setSaved(false);
      setLegalLanguage(targetLanguage);
      setLegalTranslationProgress(100);
    } catch (translateError) {
      setError(translateError?.response?.data?.detail || translateError?.message || t("shop_translate_error", { defaultValue: "Не удалось выполнить автоматический перевод" }));
    } finally {
      setLegalTranslationBusy("");
      window.setTimeout(() => setLegalTranslationProgress(0), 600);
    }
  };

  const [legalPreview, setLegalPreview] = useState(null);

  const insertLegalTemplate = (page) => {
    const currentValue = String(form.legal?.[page]?.[legalLanguage] || "").trim();
    if (currentValue && !window.confirm(t("shop_template_overwrite_confirm", { defaultValue: "В поле уже есть содержимое. Заменить его HTML-шаблоном?" }))) return;
    setLegalField(page, legalLanguage, buildLegalTemplate(page, legalLanguage));
  };

  const setCookieField = (name, value) => {
    setSaved(false);
    setForm((current) => ({ ...current, cookie_settings: { ...current.cookie_settings, [name]: value } }));
  };

  const setField = (name, value) => {
    setSaved(false);
    setForm((current) => ({ ...current, [name]: value }));
  };

  const setCommerceModule = (name, value) => {
    setSaved(false);
    setForm((current) => {
      const nextModules = { ...current.commerce_modules, [name]: value };
      if (!nextModules.shop && !nextModules.auction && !nextModules.marketplace) nextModules[name] = true;
      const salesMode = nextModules.auction ? (nextModules.shop ? "hybrid" : "auction") : "shop";
      return { ...current, commerce_modules: nextModules, sales_mode: salesMode };
    });
  };

  const setThemeField = (name, value) => {
    setSaved(false);
    setForm((current) => {
      const next = { ...current, [name]: value };
      if (current.auto_text_contrast && THEME_BACKGROUND_FIELDS.has(name) && /^#[0-9a-fA-F]{6}$/.test(value)) {
        Object.assign(next, autoThemeColors(next));
      }
      return next;
    });
  };

  const setAutoTextContrast = (checked) => {
    setSaved(false);
    setForm((current) => {
      const next = { ...current, auto_text_contrast: checked };
      if (checked) Object.assign(next, autoThemeColors(next));
      return next;
    });
  };

  const setWorkflowField = (name, value) => {
    setSaved(false);
    setForm((current) => ({
      ...current,
      crm_workflow: { ...current.crm_workflow, [name]: value },
    }));
  };


  const updateContactField = (name, value) => {
    setSaved(false);
    setForm((current) => ({
      ...current,
      contact_details: { ...current.contact_details, [name]: value },
    }));
  };

  const updateContactLocalizedField = (name, language, value) => {
    setSaved(false);
    setForm((current) => ({
      ...current,
      contact_details: {
        ...current.contact_details,
        [name]: { ...current.contact_details[name], [language]: value },
      },
    }));
  };

  const addContactExtraField = () => {
    setSaved(false);
    setForm((current) => ({
      ...current,
      contact_details: {
        ...current.contact_details,
        extra_fields: [
          ...current.contact_details.extra_fields,
          { id: createId("contact"), label: { ru: "", en: "" }, value: { ru: "", en: "" } },
        ],
      },
    }));
  };

  const updateContactExtraField = (id, part, language, value) => {
    setSaved(false);
    setForm((current) => ({
      ...current,
      contact_details: {
        ...current.contact_details,
        extra_fields: current.contact_details.extra_fields.map((field) =>
          field.id === id
            ? { ...field, [part]: { ...field[part], [language]: value } }
            : field
        ),
      },
    }));
  };

  const removeContactExtraField = (id) => {
    setSaved(false);
    setForm((current) => ({
      ...current,
      contact_details: {
        ...current.contact_details,
        extra_fields: current.contact_details.extra_fields.filter((field) => field.id !== id),
      },
    }));
  };

  const addServiceLocation = () => {
    setSaved(false);
    setForm((current) => ({
      ...current,
      service_locations: [
        ...current.service_locations,
        {
          id: createId("location"),
          name: { ru: "", en: "" },
          city: { ru: "", en: "" },
          address: { ru: "", en: "" },
          phone: current.contact_details.phone || "",
          email: current.contact_details.email || "",
          working_hours: { ru: "", en: "" },
          latitude: "",
          longitude: "",
          extra_fields: [],
        },
      ],
    }));
  };

  const updateServiceLocation = (id, name, value, language = null) => {
    setSaved(false);
    setForm((current) => ({
      ...current,
      service_locations: current.service_locations.map((location) => {
        if (location.id !== id) return location;
        if (language) return { ...location, [name]: { ...location[name], [language]: value } };
        return { ...location, [name]: value };
      }),
    }));
  };

  const removeServiceLocation = (id) => {
    setSaved(false);
    setForm((current) => ({
      ...current,
      service_locations: current.service_locations.filter((location) => location.id !== id),
    }));
  };

  const addLocationExtraField = (locationId) => {
    setSaved(false);
    setForm((current) => ({
      ...current,
      service_locations: current.service_locations.map((location) =>
        location.id === locationId
          ? {
              ...location,
              extra_fields: [
                ...location.extra_fields,
                { id: createId("location-field"), label: { ru: "", en: "" }, value: { ru: "", en: "" } },
              ],
            }
          : location
      ),
    }));
  };

  const updateLocationExtraField = (locationId, fieldId, part, language, value) => {
    setSaved(false);
    setForm((current) => ({
      ...current,
      service_locations: current.service_locations.map((location) =>
        location.id === locationId
          ? {
              ...location,
              extra_fields: location.extra_fields.map((field) =>
                field.id === fieldId
                  ? { ...field, [part]: { ...field[part], [language]: value } }
                  : field
              ),
            }
          : location
      ),
    }));
  };

  const removeLocationExtraField = (locationId, fieldId) => {
    setSaved(false);
    setForm((current) => ({
      ...current,
      service_locations: current.service_locations.map((location) =>
        location.id === locationId
          ? { ...location, extra_fields: location.extra_fields.filter((field) => field.id !== fieldId) }
          : location
      ),
    }));
  };

  const commitAssetState = (changes) => {
    setForm((current) => ({ ...current, ...changes }));
    setBaseline((current) => ({ ...current, ...changes }));
  };

  const uploadAsset = async (kind, file) => {
    if (!file) return;
    setError("");
    setAssetBusy(kind);
    try {
      const response =
        kind === "logo"
          ? await crmSettingsApi.uploadStorefrontLogo(file)
          : await crmSettingsApi.uploadStorefrontFavicon(file);
      commitAssetState({
        use_crm_assets: false,
        [`${kind}_key`]: response[`${kind}_key`] || "",
        [`${kind}_url`]: response[`${kind}_url`] || "",
      });
    } catch (requestError) {
      setError(
        requestError?.response?.data?.detail || t("shop_settings_asset_upload_error")
      );
    } finally {
      setAssetBusy("");
    }
  };

  const removeAsset = async (kind) => {
    setError("");
    setAssetBusy(kind);
    try {
      if (kind === "logo") await crmSettingsApi.removeStorefrontLogo();
      else await crmSettingsApi.removeStorefrontFavicon();
      commitAssetState({ [`${kind}_key`]: "", [`${kind}_url`]: "" });
    } catch (requestError) {
      setError(
        requestError?.response?.data?.detail || t("shop_settings_asset_remove_error")
      );
    } finally {
      setAssetBusy("");
    }
  };

  const validate = () => {
    if (form.enabled && !form.store_name.trim()) return t("shop_settings_validation_name");
    if (!SUPPORTED_SHOP_CURRENCIES.some((item) => item.code === form.currency.trim().toUpperCase())) {
      return t("shop_settings_validation_currency");
    }
    if (
      form.support_email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.support_email.trim())
    ) {
      return t("shop_settings_validation_email");
    }
    if (
      !isHttpUrl(form.public_url) ||
      (!form.logo_key && !isHttpUrl(form.logo_url)) ||
      (!form.favicon_key && !isHttpUrl(form.favicon_url))
    ) {
      return t("shop_settings_validation_url");
    }
    if (
      Object.keys(LIGHT_THEME_DEFAULTS).some(
        (field) => !/^#[0-9a-fA-F]{6}$/.test(form[field])
      )
    ) {
      return t("shop_settings_validation_color");
    }
    for (const location of form.service_locations) {
      const hasLat = String(location.latitude || "").trim() !== "";
      const hasLng = String(location.longitude || "").trim() !== "";
      if (hasLat !== hasLng) return t("shop_settings_location_coordinates_required");
      if (hasLat) {
        const lat = Number(location.latitude);
        const lng = Number(location.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
          return t("shop_settings_location_coordinates_invalid");
        }
      }
    }
    return "";
  };


  const updateHomepage = (updater) => {
    setSaved(false);
    setForm((current) => ({
      ...current,
      homepage: typeof updater === "function" ? updater(current.homepage) : updater,
    }));
  };

  const addHomepageSlide = () => {
    updateHomepage((homepage) => ({
      ...homepage,
      slides: [
        ...homepage.slides,
        { id: createId("slide"), enabled: true, attachment_id: null, mobile_attachment_id: null, preview_url: "", mobile_preview_url: "", title: { ru: "", en: "" }, text: { ru: "", en: "" }, button: { ru: "Подробнее", en: "Explore" }, link_type: "none", target_id: null, url: "" },
      ],
    }));
  };

  const updateHomepageSlide = (id, patch) => {
    updateHomepage((homepage) => ({
      ...homepage,
      slides: homepage.slides.map((slide) => slide.id === id ? { ...slide, ...patch } : slide),
    }));
  };

  const removeHomepageSlide = async (slide) => {
    setHomepageBusy(`remove-${slide.id}`);
    try {
      if (slide.attachment_id) await crmSettingsApi.deleteStorageFile(slide.attachment_id).catch(() => null);
      if (slide.mobile_attachment_id) await crmSettingsApi.deleteStorageFile(slide.mobile_attachment_id).catch(() => null);
      updateHomepage((homepage) => ({ ...homepage, slides: homepage.slides.filter((item) => item.id !== slide.id) }));
    } finally {
      setHomepageBusy("");
    }
  };

  const uploadHomepageSlide = async (slide, file, mobile = false) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) { setError(t("shop_homepage_image_type_error")); return; }
    if (file.size > 15 * 1024 * 1024) { setError(t("shop_homepage_image_size_error")); return; }
    const busyKey = `${mobile ? "mobile" : "desktop"}-${slide.id}`;
    setHomepageBusy(busyKey);
    setError("");
    try {
      const uploaded = await crmSettingsApi.uploadStorageFile(file, { entity_type: "storefront_homepage", entity_id: 0 });
      const oldId = mobile ? slide.mobile_attachment_id : slide.attachment_id;
      if (oldId) await crmSettingsApi.deleteStorageFile(oldId).catch(() => null);
      const preview = URL.createObjectURL(file);
      updateHomepageSlide(slide.id, mobile
        ? { mobile_attachment_id: uploaded.id, mobile_preview_url: preview }
        : { attachment_id: uploaded.id, preview_url: preview });
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || t("shop_homepage_upload_error"));
    } finally {
      setHomepageBusy("");
    }
  };

  const uploadHomepageCategoryImage = async (categoryId, file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) { setError(t("shop_homepage_image_type_error")); return; }
    if (file.size > 15 * 1024 * 1024) { setError(t("shop_homepage_image_size_error")); return; }
    const key = String(categoryId);
    setHomepageBusy(`category-${key}`);
    setHomepageUploadProgress((current) => ({ ...current, [key]: 0 }));
    setError("");
    try {
      const uploaded = await crmSettingsApi.uploadStorageFile(
        file,
        { entity_type: "storefront_homepage", entity_id: 0 },
        (percent) => setHomepageUploadProgress((current) => ({ ...current, [key]: percent }))
      );
      setHomepageUploadProgress((current) => ({ ...current, [key]: 100 }));
      const oldId = form.homepage?.category_images?.[key]?.attachment_id;
      if (oldId) await crmSettingsApi.deleteStorageFile(oldId).catch(() => null);
      const preview = URL.createObjectURL(file);
      updateHomepage((homepage) => ({
        ...homepage,
        category_images: {
          ...(homepage.category_images || {}),
          [key]: { attachment_id: uploaded.id, preview_url: preview },
        },
      }));
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || t("shop_homepage_upload_error"));
    } finally {
      setHomepageBusy("");
      window.setTimeout(() => {
        setHomepageUploadProgress((current) => {
          const next = { ...current };
          delete next[key];
          return next;
        });
      }, 450);
    }
  };

  const removeHomepageCategoryImage = async (categoryId) => {
    const key = String(categoryId);
    const current = form.homepage?.category_images?.[key];
    if (!current?.attachment_id) return;
    setHomepageBusy(`category-${key}`);
    try {
      await crmSettingsApi.deleteStorageFile(current.attachment_id).catch(() => null);
      updateHomepage((homepage) => {
        const next = { ...(homepage.category_images || {}) };
        delete next[key];
        return { ...homepage, category_images: next };
      });
    } finally {
      setHomepageBusy("");
    }
  };

  const toggleHomepageCategory = (categoryId) => {
    updateHomepage((homepage) => {
      const ids = homepage.catalog_menu.category_ids || [];
      return {
        ...homepage,
        catalog_menu: {
          ...homepage.catalog_menu,
          category_ids: ids.includes(categoryId) ? ids.filter((id) => id !== categoryId) : [...ids, categoryId],
        },
      };
    });
  };

  const moveHomepageSection = (index, direction) => {
    updateHomepage((homepage) => {
      const next = [...homepage.sections];
      const target = index + direction;
      if (target < 0 || target >= next.length) return homepage;
      [next[index], next[target]] = [next[target], next[index]];
      return { ...homepage, sections: next };
    });
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setSaved(false);
    setError("");
    const modules = {
      shop: form.commerce_modules?.shop !== false,
      auction: Boolean(form.commerce_modules?.auction),
      marketplace: Boolean(form.commerce_modules?.marketplace),
    };
    const derivedSalesMode = modules.auction ? (modules.shop ? "hybrid" : "auction") : "shop";
    const payload = {
      ...form,
      commerce_modules: modules,
      sales_mode: derivedSalesMode,
      marketplace: {
        ...form.marketplace,
        default_listing_days: Math.max(1, Math.min(365, Number(form.marketplace?.default_listing_days || 30))),
        max_images: Math.max(1, Math.min(20, Number(form.marketplace?.max_images || 8))),
        user_listings: {
          ...form.marketplace?.user_listings,
          enabled: Boolean(form.marketplace?.user_listings?.enabled),
          free_per_week: Math.max(0, Math.min(100, Number(form.marketplace?.user_listings?.free_per_week ?? 3))),
          max_active: Math.max(1, Math.min(500, Number(form.marketplace?.user_listings?.max_active ?? 10))),
          max_photos: Math.max(1, Math.min(20, Number(form.marketplace?.user_listings?.max_photos ?? 8))),
          lifetime_days: Math.max(1, Math.min(365, Number(form.marketplace?.user_listings?.lifetime_days ?? 30))),
        },
      },
      currency: form.currency.trim().toUpperCase(),
      public_url: form.public_url.trim(),
      store_name: form.store_name.trim(),
      support_email: form.contact_details.email.trim() || form.support_email.trim(),
      support_phone: form.contact_details.phone.trim() || form.support_phone.trim(),
      address: form.contact_details.address.trim() || form.address.trim(),
      contact_details: {
        ...form.contact_details,
        legal_name: form.contact_details.legal_name.trim(),
        registry_code: form.contact_details.registry_code.trim(),
        vat_number: form.contact_details.vat_number.trim(),
        email: form.contact_details.email.trim(),
        phone: form.contact_details.phone.trim(),
        address: form.contact_details.address.trim(),
        city: form.contact_details.city.trim(),
        postal_code: form.contact_details.postal_code.trim(),
        working_hours: cleanLocalized(form.contact_details.working_hours),
        extra_fields: form.contact_details.extra_fields
          .map((field) => ({
            id: field.id,
            label: cleanLocalized(field.label),
            value: cleanLocalized(field.value),
          }))
          .filter((field) => field.label.ru || field.label.en || field.value.ru || field.value.en),
      },
      service_locations: form.service_locations.map((location) => ({
        ...location,
        name: cleanLocalized(location.name),
        city: cleanLocalized(location.city),
        address: cleanLocalized(location.address),
        working_hours: cleanLocalized(location.working_hours),
        phone: location.phone.trim(),
        email: location.email.trim(),
        latitude: location.latitude === "" ? null : Number(location.latitude),
        longitude: location.longitude === "" ? null : Number(location.longitude),
        extra_fields: location.extra_fields
          .map((field) => ({
            id: field.id,
            label: cleanLocalized(field.label),
            value: cleanLocalized(field.value),
          }))
          .filter((field) => field.label.ru || field.label.en || field.value.ru || field.value.en),
      })),
      legal: Object.fromEntries(Object.entries(form.legal).map(([key, value]) => [key, cleanLocalized(value)])),
      cookie_settings: {
        ...form.cookie_settings,
        title: form.cookie_settings.title.trim(),
        text: form.cookie_settings.text.trim(),
      },
      homepage: {
        ...form.homepage,
        category_images: Object.fromEntries(
          Object.entries(form.homepage.category_images || {})
            .map(([categoryId, value]) => [String(categoryId), { attachment_id: Number(value?.attachment_id || 0) || null }])
            .filter(([, value]) => value.attachment_id)
        ),
        catalog_menu: {
          ...form.homepage.catalog_menu,
          category_ids: form.homepage.catalog_menu.category_ids.map(Number).filter(Boolean),
        },
        slides: form.homepage.slides.map((slide) => ({
          id: slide.id, enabled: slide.enabled, attachment_id: slide.attachment_id, mobile_attachment_id: slide.mobile_attachment_id,
          title: cleanLocalized(slide.title), text: cleanLocalized(slide.text), button: cleanLocalized(slide.button),
          link_type: slide.link_type, target_id: slide.target_id || null, url: slide.url.trim() || null,
        })),
        sections: form.homepage.sections.map((section) => ({
          ...section, title: cleanLocalized(section.title), text: cleanLocalized(section.text), button: cleanLocalized(section.button),
        })),
      },
      logo_url: form.logo_key ? null : form.logo_url.trim() || null,
      favicon_url: form.favicon_key ? null : form.favicon_url.trim() || null,
      crm_workflow: {
        ...form.crm_workflow,
        enabled: true,
        owner_id: form.crm_workflow.owner_id
          ? Number(form.crm_workflow.owner_id)
          : null,
        task_due_minutes: Math.min(
          10080,
          Math.max(0, Number(form.crm_workflow.task_due_minutes) || 0)
        ),
        lead_source_name: form.crm_workflow.lead_source_name.trim() || "VelShop",
        task_title: form.crm_workflow.task_title.trim() || null,
      },
    };

    try {
      await crmSettingsApi.update({ settings_json: { storefront: payload } });
      const next = normalize({
        company_name: crmAssets.company_name,
        logo_url: crmAssets.logo_url,
        favicon_url: crmAssets.favicon_url,
        settings_json: {
          storefront: {
            ...payload,
            logo_url: form.logo_url,
            favicon_url: form.favicon_url,
          },
        },
      }).form;
      setForm(next);
      setBaseline(next);
      setSaved(true);
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || t("shop_settings_save_error"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className={styles.state}>{t("shop_settings_loading")}</div>;

  const previewLogo = form.use_crm_assets
    ? crmAssets.logo_url
    : form.logo_url || crmAssets.logo_url;
  const previewFavicon = form.use_crm_assets
    ? crmAssets.favicon_url
    : form.favicon_url || crmAssets.favicon_url;

  return (
    <main className={styles.page}>
      <header className={styles.topBar}>
        <div>
          <p className={styles.eyebrow}>{t("shop_settings")}</p>
          <h1>{t("shop_settings_title")}</h1>
          <p className={styles.subtitle}>{t("shop_settings_subtitle")}</p>
        </div>
        <div className={styles.actions}>
          {form.public_url ? (
            <button
              className={styles.secondaryButton}
              type="button"
              onClick={() => window.open(form.public_url, "_blank", "noopener,noreferrer")}
            >
              {t("shop_settings_open_store")}
            </button>
          ) : null}
          <button
            className={styles.secondaryButton}
            type="button"
            disabled={!isDirty || saving}
            onClick={() => {
              setForm(baseline);
              setError("");
              setSaved(false);
            }}
          >
            {t("shop_settings_reset")}
          </button>
          <button
            className={styles.primaryButton}
            type="button"
            disabled={!isDirty || saving}
            onClick={handleSave}
          >
            {saving ? t("shop_settings_saving") : t("shop_settings_save")}
          </button>
        </div>
      </header>

      <>
        <nav className={styles.settingsSectionNav} aria-label={t("shop_ui_retailshop_settings_sections")}>
          {settingsSections.map((section) => (
            <button
              key={section.key}
              type="button"
              className={`${styles.settingsSectionButton} ${activeSection === section.key ? styles.settingsSectionButtonActive : ""}`}
              onClick={() => { setActiveSection(section.key); setActiveTab((SETTINGS_GROUP_TABS[section.key] || ["general"])[0]); }}
            >
              <strong>{section.label}</strong><small>{section.hint}</small>
            </button>
          ))}
        </nav>
        <ResponsiveSettingsTabs
          tabs={tabs}
          activeKey={activeTab}
          onChange={setActiveTab}
          ariaLabel={t("shop_settings_title")}
          dataAcademyPrefix="shop-settings"
        />
      </>

      {error ? <div className={styles.error}>{error}</div> : null}
      {saved ? <div className={styles.success}>{t("shop_settings_saved")}</div> : null}

      <section className={styles.panel}>
        {activeTab === "branding" ? (
          <BrandingPanel
            form={form}
            homepage={form.homepage}
            crmAssets={crmAssets}
            previewLogo={previewLogo}
            previewFavicon={previewFavicon}
            assetBusy={assetBusy}
            setField={setField}
            setThemeField={setThemeField}
            setAutoTextContrast={setAutoTextContrast}
            uploadAsset={uploadAsset}
            removeAsset={removeAsset}
            resetTheme={() => {
              setSaved(false);
              setForm((current) => ({ ...current, ...LIGHT_THEME_DEFAULTS, auto_text_contrast: true }));
            }}
            t={t}
          />
        ) : null}

        {activeTab === "general" ? (
          <SettingsCard
            title={t("shop_settings_general_title")}
            subtitle={t("shop_settings_general_hint")}
          >
            <div className={styles.moduleManagementHint}>
              <strong>{t("shop_ui_retailshop_sections_are_managed_in_crm_settings_modules")}</strong>
              <span>{t("shop_ui_only_settings_for_sections_enabled_for_this_client_are_shown_here")}</span>
            </div>
            <div className={styles.formGrid}>
              <Field label={t("shop_settings_store_name")} required>
                <input value={form.store_name} onChange={(e) => setField("store_name", e.target.value)} />
              </Field>
              <Field label={t("shop_settings_tagline")}>
                <input value={form.tagline} onChange={(e) => setField("tagline", e.target.value)} />
              </Field>
              <Field label={t("shop_settings_public_url")} className={styles.fullWidth}>
                <input type="url" placeholder="https://shop.example.com" value={form.public_url} onChange={(e) => setField("public_url", e.target.value)} />
              </Field>
              <Field label={t("shop_settings_currency")}>
                <select value={form.currency} onChange={(e) => setField("currency", e.target.value)}>
                  {SUPPORTED_SHOP_CURRENCIES.map((currency) => (
                    <option key={currency.code} value={currency.code}>{currency.label}</option>
                  ))}
                </select>
              </Field>
              <Field label={t("shop_settings_locale")}>
                <select value={form.locale} onChange={(e) => setField("locale", e.target.value)}>
                  <option value="ru">{t("language_russian")}</option>
                  <option value="en">{t("language_english")}</option>
                </select>
              </Field>
              <ToggleCard
                label={t("shop_settings_publish_default")}
                hint={t("shop_settings_publish_default_hint")}
                checked={form.publish_products_by_default}
                onChange={(checked) => setField("publish_products_by_default", checked)}
              />
            </div>
          </SettingsCard>
        ) : null}


        {activeTab === "auction" ? (
          <AuctionPanel form={form} setForm={setForm} shopText={shopText} currencySymbol={shopCurrencySymbol(form.currency)} />
        ) : null}

        {activeTab === "marketplace" ? (
          <MarketplaceAdminPanel form={form} setForm={setForm} shopText={shopText} currencySymbol={shopCurrencySymbol(form.currency)} categories={shopCategories} />
        ) : null}

        {activeTab === "user_listings" ? (
          <MarketplaceUserListingsPanel form={form} setForm={setForm} shopText={shopText} />
        ) : null}

        {activeTab === "checkout" ? (
          <SettingsCard
            title={t("shop_ui_delivery_payment")}
            subtitle={t("shop_ui_configure_available_delivery_methods_delivery_fees_and_payment_methods")}
          >
            <div className={styles.checkoutSettingsGrid}>
              {[
                ["pickup", t("shop_ui_pickup"), t("shop_ui_collect_the_order_from_a_selected_service_center")],
                ["courier", t("shop_ui_courier_delivery"), t("shop_ui_delivery_to_the_customer_s_address")],
                ["parcel", t("shop_ui_parcel_machine_pickup_point"), t("shop_ui_delivery_to_a_parcel_machine_or_pickup_point")],
              ].map(([key, label, hint]) => {
                const config = form.checkout_config?.delivery?.[key] || {};
                const setDelivery = (field, value) => setForm((current) => ({
                  ...current,
                  checkout_config: {
                    ...current.checkout_config,
                    delivery: {
                      ...current.checkout_config.delivery,
                      [key]: { ...current.checkout_config.delivery[key], [field]: value },
                    },
                  },
                }));
                return <div className={styles.checkoutMethodCard} key={key}>
                  <ToggleCard label={label} hint={hint} checked={config.enabled !== false} onChange={(checked) => setDelivery("enabled", checked)} />
                  {config.enabled !== false ? <div className={styles.checkoutMethodFields}>
                    <Field label={t("shop_ui_price")}><div className={styles.moneyInput}><input type="number" min="0" step="0.01" value={config.price ?? 0} onChange={(e) => setDelivery("price", Math.max(0, Number(e.target.value) || 0))} /><span>{shopCurrencySymbol(form.currency)}</span></div></Field>
                    {key !== "pickup" ? <Field label={t("shop_ui_free_from")}><div className={styles.moneyInput}><input type="number" min="0" step="0.01" value={config.free_from ?? 0} onChange={(e) => setDelivery("free_from", Math.max(0, Number(e.target.value) || 0))} /><span>{shopCurrencySymbol(form.currency)}</span></div><small>{t("shop_ui_0_free_delivery_threshold_disabled")}</small></Field> : null}
                  </div> : null}
                </div>;
              })}
            </div>
            <div className={styles.paymentSettingsBlock}>
              <h3>{t("shop_ui_payment_methods")}</h3>
              <p>{t("shop_ui_customers_will_only_see_enabled_options")}</p>
              <div className={styles.paymentSettingsGrid}>
                <ToggleCard label={t("shop_ui_bank_transfer")} hint={t("shop_ui_payment_details_can_be_provided_after_the_order_is_placed")} checked={form.checkout_config?.payment?.bank_transfer !== false} onChange={(checked) => setForm((current) => ({ ...current, checkout_config: { ...current.checkout_config, payment: { ...current.checkout_config.payment, bank_transfer: checked } } }))} />
                <ToggleCard label={t("shop_ui_pay_on_pickup")} hint={t("shop_ui_available_only_for_pickup_orders")} checked={form.checkout_config?.payment?.pay_on_pickup !== false} onChange={(checked) => setForm((current) => ({ ...current, checkout_config: { ...current.checkout_config, payment: { ...current.checkout_config.payment, pay_on_pickup: checked } } }))} />
              </div>
              <div className={styles.montonioSettings}>
                <div className={styles.montonioHeader}>
                  <div>
                    <strong>Montonio</strong>
                    <span>{t("shop_ui_online_bank_payments_with_automatic_order_confirmation")}</span>
                  </div>
                  <label className={styles.montonioSwitch}>
                    <input type="checkbox" checked={form.checkout_config?.payment?.montonio?.enabled === true} onChange={(e) => setForm((current) => ({ ...current, checkout_config: { ...current.checkout_config, payment: { ...current.checkout_config.payment, montonio: { ...current.checkout_config.payment.montonio, enabled: e.target.checked } } } }))} />
                    <span>{t("shop_ui_enable")}</span>
                  </label>
                </div>
                {form.checkout_config?.payment?.montonio?.enabled === true ? (
                  <>
                    {form.currency !== "EUR" ? <div className={styles.montonioWarning}>{t("shop_ui_montonio_bank_payments_require_eur_select_eur_in_the_general_tab")}</div> : null}
                    <div className={styles.montonioGrid}>
                      <Field label={t("shop_ui_environment")}>
                        <select value={form.checkout_config.payment.montonio.environment} onChange={(e) => setForm((current) => ({ ...current, checkout_config: { ...current.checkout_config, payment: { ...current.checkout_config.payment, montonio: { ...current.checkout_config.payment.montonio, environment: e.target.value } } } }))}>
                          <option value="sandbox">{t("shop_montonio_sandbox")}</option>
                          <option value="production">{t("shop_montonio_production")}</option>
                        </select>
                      </Field>
                      <Field label={t("shop_ui_bank_country")}>
                        <select value={form.checkout_config.payment.montonio.country} onChange={(e) => setForm((current) => ({ ...current, checkout_config: { ...current.checkout_config, payment: { ...current.checkout_config.payment, montonio: { ...current.checkout_config.payment.montonio, country: e.target.value } } } }))}>
                          <option value="EE">{t("country_estonia")}</option><option value="LV">{t("country_latvia")}</option><option value="LT">{t("country_lithuania")}</option><option value="FI">{t("country_finland")}</option><option value="PL">{t("country_poland")}</option><option value="DE">{t("country_germany")}</option>
                        </select>
                      </Field>
                      <Field label={t("shop_montonio_access_key")}>
                        <input autoComplete="off" value={form.checkout_config.payment.montonio.access_key} onChange={(e) => setForm((current) => ({ ...current, checkout_config: { ...current.checkout_config, payment: { ...current.checkout_config.payment, montonio: { ...current.checkout_config.payment.montonio, access_key: e.target.value } } } }))} placeholder={t("shop_montonio_access_key_placeholder")} />
                      </Field>
                      <Field label={t("shop_montonio_secret_key")}>
                        <input type="password" autoComplete="new-password" value={form.checkout_config.payment.montonio.secret_key} onChange={(e) => setForm((current) => ({ ...current, checkout_config: { ...current.checkout_config, payment: { ...current.checkout_config.payment, montonio: { ...current.checkout_config.payment.montonio, secret_key: e.target.value } } } }))} placeholder={t("shop_montonio_secret_key_placeholder")} />
                      </Field>
                    </div>
                    <small className={styles.montonioNote}>{t("shop_ui_keys_are_stored_in_crm_settings_and_are_never_exposed_by_the_public_st")}</small>
                  </>
                ) : null}
              </div>
            </div>
          </SettingsCard>
        ) : null}

        {activeTab === "homepage" ? (
          <HomepagePanel
            homepage={form.homepage}
            categories={shopCategories}
            catalogTree={shopCatalogTree}
            busy={homepageBusy}
            uploadProgress={homepageUploadProgress}
            updateHomepage={updateHomepage}
            addSlide={addHomepageSlide}
            updateSlide={updateHomepageSlide}
            removeSlide={removeHomepageSlide}
            uploadSlide={uploadHomepageSlide}
            uploadCategoryImage={uploadHomepageCategoryImage}
            removeCategoryImage={removeHomepageCategoryImage}
            toggleCategory={toggleHomepageCategory}
            moveSection={moveHomepageSection}
            t={t}
          />
        ) : null}

        {activeTab === "contacts" ? (
          <ContactsAndLocationsPanel
            contact={form.contact_details}
            locations={form.service_locations}
            updateContactField={updateContactField}
            updateContactLocalizedField={updateContactLocalizedField}
            addContactExtraField={addContactExtraField}
            updateContactExtraField={updateContactExtraField}
            removeContactExtraField={removeContactExtraField}
            addServiceLocation={addServiceLocation}
            updateServiceLocation={updateServiceLocation}
            removeServiceLocation={removeServiceLocation}
            addLocationExtraField={addLocationExtraField}
            updateLocationExtraField={updateLocationExtraField}
            removeLocationExtraField={removeLocationExtraField}
            t={t}
          />
        ) : null}

        {activeTab === "content" ? (
          <div className={styles.legalEditor}>
            <SettingsCard title={t("shop_legal_title")} subtitle={t("shop_legal_hint")}>
              <div className={styles.legalLanguageToolbar}>
                <div className={styles.legalLanguageTabs}>
                  {legalLanguages.map((language) => (
                    <div
                      className={`${styles.legalLanguageTabWrap} ${legalLanguage === language.code ? styles.legalLanguageTabActive : ""}`}
                      key={language.code}
                    >
                      <button
                        type="button"
                        className={styles.legalLanguageTab}
                        onClick={() => setLegalLanguage(language.code)}
                      >
                        <strong>{language.short}</strong>
                        <span>{language.label}</span>
                      </button>
                      {legalLanguage !== language.code ? (
                        <button
                          type="button"
                          className={styles.legalTranslateButton}
                          onClick={() => translateLegalPages(language.code)}
                          disabled={Boolean(legalTranslationBusy)}
                          title={t("shop_translate_to_language", { defaultValue: "Перевести все текстовые поля на этот язык" })}
                        >
                          {legalTranslationBusy === language.code ? (
                            <span className={styles.legalTranslateSpinner} aria-hidden="true" />
                          ) : (
                            <span className={styles.legalTranslateSpark} aria-hidden="true">✦</span>
                          )}
                          <span>
                            {legalTranslationBusy === language.code
                              ? `${t("translating", { defaultValue: "Перевод…" })} ${Math.round(legalTranslationProgress)}%`
                              : t("translate", { defaultValue: "Перевести" })}
                          </span>
                        </button>
                      ) : null}
                    </div>
                  ))}
                </div>
                <p className={styles.legalLanguageHint}>
                  {t("shop_translate_hint", {
                    defaultValue: "Заполните страницы на основном языке, затем нажмите «Перевести» на нужном языке. Таб открывает ручное редактирование.",
                  })}
                </p>
                {legalTranslationBusy ? (
                  <div className={styles.legalTranslationProgress} aria-live="polite">
                    <span style={{ width: `${Math.max(2, legalTranslationProgress)}%` }} />
                  </div>
                ) : null}
              </div>

              <div className={styles.legalGrid}>
                {["about", "delivery", "privacy", "terms", "returns", "cookies"].map((page) => {
                  const activeLanguage = legalLanguages.find((item) => item.code === legalLanguage);
                  return (
                    <article className={styles.legalCard} key={page}>
                      <header>
                        <strong>{t(`shop_legal_${page}`)}</strong>
                        <span>{activeLanguage?.short || legalLanguage.toUpperCase()}</span>
                      </header>
                      <div className={styles.legalCardActions}>
                        <button type="button" className={styles.legalTemplateButton} onClick={() => insertLegalTemplate(page)}>
                          <span aria-hidden="true">◇</span>
                          {t("shop_insert_html_template", { defaultValue: "HTML-шаблон" })}
                        </button>
                        <button
                          type="button"
                          className={styles.legalPreviewButton}
                          onClick={() => setLegalPreview({ page, html: form.legal[page]?.[legalLanguage] || "", language: legalLanguage })}
                        >
                          <span aria-hidden="true">◉</span>
                          {t("preview", { defaultValue: "Превью" })}
                        </button>
                      </div>
                      <Field label={`${t(`shop_legal_${page}`)} · HTML · ${activeLanguage?.short || legalLanguage.toUpperCase()}`}>
                        <textarea
                          className={styles.legalHtmlEditor}
                          rows="10"
                          spellCheck="false"
                          value={form.legal[page]?.[legalLanguage] || ""}
                          onChange={(e) => setLegalField(page, legalLanguage, e.target.value)}
                          placeholder={t("shop_html_editor_placeholder", { defaultValue: "Введите HTML или нажмите «HTML-шаблон»…" })}
                        />
                      </Field>
                    </article>
                  );
                })}
              </div>
            </SettingsCard>
            {legalPreview ? (
              <div className={styles.legalPreviewBackdrop} onMouseDown={(event) => { if (event.target === event.currentTarget) setLegalPreview(null); }}>
                <div className={styles.legalPreviewModal} role="dialog" aria-modal="true">
                  <div className={styles.legalPreviewHeader}>
                    <div>
                      <span>{t("shop_preview_storefront", { defaultValue: "Предпросмотр страницы магазина" })}</span>
                      <strong>{t(`shop_legal_${legalPreview.page}`)} · {String(legalPreview.language || "").toUpperCase()}</strong>
                    </div>
                    <button type="button" onClick={() => setLegalPreview(null)} aria-label={t("close", { defaultValue: "Закрыть" })}>×</button>
                  </div>
                  <div className={styles.legalPreviewCanvas}>
                    <div className={styles.legalPreviewStoreCard}>
                      <span className={styles.legalPreviewEyebrow}>{form.store_name || "VelShop"}</span>
                      <h1>{t(`shop_legal_${legalPreview.page}`)}</h1>
                      <div className={styles.legalPreviewHtml} dangerouslySetInnerHTML={{ __html: sanitizeLegalHtml(legalPreview.html || `<p>${t("shop_preview_empty", { defaultValue: "Нет содержимого для предпросмотра" })}</p>`) }} />
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
            <SettingsCard title={t("shop_cookie_title")} subtitle={t("shop_cookie_hint")}>
              <div className={styles.formGrid}>
                <ToggleCard label={t("shop_cookie_enabled")} hint={t("shop_cookie_enabled_hint")} checked={form.cookie_settings.enabled} onChange={(value) => setCookieField("enabled", value)} />
                <Field label={t("shop_cookie_banner_title")}><input value={form.cookie_settings.title} onChange={(e) => setCookieField("title", e.target.value)} placeholder={t("shop_cookie_banner_title_placeholder")} /></Field>
                <Field label={t("shop_cookie_banner_text")} className={styles.fullWidth}><textarea rows="4" value={form.cookie_settings.text} onChange={(e) => setCookieField("text", e.target.value)} placeholder={t("shop_cookie_banner_text_placeholder")} /></Field>
                <ToggleCard label={t("shop_cookie_analytics")} hint={t("shop_cookie_analytics_hint")} checked={form.cookie_settings.analytics_enabled} onChange={(value) => setCookieField("analytics_enabled", value)} />
                <ToggleCard label={t("shop_cookie_marketing")} hint={t("shop_cookie_marketing_hint")} checked={form.cookie_settings.marketing_enabled} onChange={(value) => setCookieField("marketing_enabled", value)} />
              </div>
            </SettingsCard>
          </div>
        ) : null}

        {activeTab === "support" ? (
          <SettingsCard title={t("shop_support_title")} subtitle={t("shop_support_hint")}>
            {supportLoading ? <div className={styles.state}>{t("shop_support_loading")}</div> : null}
            {supportError ? <div className={styles.error}>{String(supportError)}</div> : null}
            {!supportLoading && !supportThreads.length ? <div className={styles.homepageEmpty}>{t("shop_support_empty")}</div> : null}
            <div className={styles.supportThreadList}>
              {supportThreads.map((thread) => (
                <article className={styles.supportThread} key={thread.thread_id}>
                  <header><div><small>{thread.contact?.name || t("shop_support_customer")}{thread.contact?.ui_id ? ` · #${thread.contact.ui_id}` : ""}</small><h3>{thread.product_name || thread.subject}</h3><span>{thread.subject}</span></div><time>{thread.updated_at ? new Date(thread.updated_at).toLocaleString() : ""}</time></header>
                  <div className={styles.supportMessages}>
                    {(thread.messages || []).map((message) => <div className={`${styles.supportMessage} ${message.direction === "out" ? styles.supportMessageOut : styles.supportMessageIn}`} key={message.id}><small>{message.direction === "out" ? t("shop_support_manager") : t("shop_support_customer")}</small><p>{message.body}</p><time>{new Date(message.created_at).toLocaleString()}</time></div>)}
                  </div>
                  <div className={styles.supportReply}><textarea rows="3" value={supportReply[thread.thread_id] || ""} onChange={(e) => setSupportReply((current) => ({ ...current, [thread.thread_id]: e.target.value }))} placeholder={t("shop_support_reply_placeholder")} /><button className={styles.primaryButton} type="button" disabled={!String(supportReply[thread.thread_id] || "").trim() || supportBusy === thread.thread_id} onClick={() => sendSupportReply(thread.thread_id)}>{supportBusy === thread.thread_id ? t("shop_support_sending") : t("shop_support_send")}</button></div>
                </article>
              ))}
            </div>
          </SettingsCard>
        ) : null}

        {activeTab === "reviews" ? (
          <SettingsCard title={t("shop_ui_store_reviews")} subtitle={t("shop_ui_moderate_customer_reviews_before_they_appear_in_the_public_store")}>
            <div className={styles.reviewAdminToolbar}>
              {[
                ["planned", t("shop_ui_pending")],
                ["done", t("shop_ui_published")],
                ["canceled", t("shop_ui_hidden")],
                ["all", t("shop_ui_all")],
              ].map(([key, label]) => <button key={key} type="button" className={reviewsFilter === key ? styles.reviewFilterActive : ""} onClick={() => setReviewsFilter(key)}>{label}</button>)}
            </div>
            {reviewsLoading ? <div className={styles.state}>{t("shop_ui_loading")}</div> : null}
            {reviewsError ? <div className={styles.error}>{String(reviewsError)}</div> : null}
            {!reviewsLoading && !shopReviews.length ? <div className={styles.homepageEmpty}>{t("shop_ui_no_reviews_in_this_section")}</div> : null}
            <div className={styles.reviewAdminList}>
              {shopReviews.map((review) => (
                <article className={styles.reviewAdminCard} key={review.id}>
                  <header><div><strong>{review.product?.name || t("shop_ui_product")}</strong><small>{review.contact?.name || t("shop_ui_customer")}{review.contact?.ui_id ? ` · #${review.contact.ui_id}` : ""}</small></div><div className={styles.reviewAdminRating}>{"★".repeat(Math.max(0, Math.min(5, Number(review.rating || 0))))}<span>{"★".repeat(Math.max(0, 5 - Math.min(5, Number(review.rating || 0))))}</span></div></header>
                  <p>{review.body}</p>
                  <footer><time>{review.created_at ? new Date(review.created_at).toLocaleString() : ""}</time><div>{review.status !== "done" ? <button type="button" className={styles.primaryButton} disabled={reviewBusy === review.id} onClick={() => moderateReview(review.id, "done")}>{t("shop_ui_publish")}</button> : null}{review.status !== "canceled" ? <button type="button" className={styles.secondaryButton} disabled={reviewBusy === review.id} onClick={() => moderateReview(review.id, "canceled")}>{t("shop_ui_hide")}</button> : null}{review.status !== "planned" ? <button type="button" className={styles.secondaryButton} disabled={reviewBusy === review.id} onClick={() => moderateReview(review.id, "planned")}>{t("shop_ui_to_moderation")}</button> : null}</div></footer>
                </article>
              ))}
            </div>
          </SettingsCard>
        ) : null}

        {activeTab === "workflow" ? (
          <SettingsCard title={t("shop_settings_workflow_title")} subtitle={t("shop_settings_workflow_hint")}>
            <div className={styles.workflowNotice}>
              <div className={styles.workflowIcon}>✓</div>
              <div><strong>{t("shop_settings_workflow_connected")}</strong><p>{t("shop_settings_workflow_chain")}</p></div>
              <span>{t("shop_settings_workflow_active")}</span>
            </div>
            <div className={styles.formGrid}>
              <Field label={t("shop_settings_workflow_source")}>
                <input value={form.crm_workflow.lead_source_name} onChange={(e) => setWorkflowField("lead_source_name", e.target.value)} />
              </Field>
              <Field label={t("shop_settings_workflow_owner")}>
                <select value={form.crm_workflow.owner_id} onChange={(e) => setWorkflowField("owner_id", e.target.value)}>
                  <option value="">{t("shop_settings_workflow_owner_auto")}</option>
                  {owners.map((owner) => <option key={owner.id} value={owner.id}>{owner.full_name} · {owner.email}</option>)}
                </select>
              </Field>
              <Field label={t("shop_settings_workflow_task_due")}>
                <input type="number" min="0" max="10080" value={form.crm_workflow.task_due_minutes} onChange={(e) => setWorkflowField("task_due_minutes", e.target.value)} />
              </Field>
              <Field label={t("shop_settings_workflow_task_title")} hint={t("shop_settings_workflow_task_title_hint")}>
                <input value={form.crm_workflow.task_title} placeholder={t("shop_workflow_task_title_placeholder")} onChange={(e) => setWorkflowField("task_title", e.target.value)} />
              </Field>
            </div>
          </SettingsCard>
        ) : null}
      </section>
    </main>
  );
}


function HomepagePanel({ homepage, categories, catalogTree = [], busy, uploadProgress = {}, updateHomepage, addSlide, updateSlide, removeSlide, uploadSlide, uploadCategoryImage, removeCategoryImage, toggleCategory, moveSection, t }) {
  const [newType, setNewType] = useState("product_carousel");
  const [dragIndex, setDragIndex] = useState(null);
  const setCatalog = (patch) => updateHomepage((current) => ({ ...current, catalog_menu: { ...current.catalog_menu, ...patch } }));
  const setSection = (id, patch) => updateHomepage((current) => ({ ...current, sections: current.sections.map((item) => item.id === id ? { ...item, ...patch } : item) }));
  const removeSection = (id) => updateHomepage((current) => ({ ...current, sections: current.sections.filter((item) => item.id !== id) }));
  const addSection = () => {
    const base = { id: createId("section"), type: newType, enabled: true, title: { ru: "", en: "" }, text: { ru: "", en: "" }, button: { ru: "", en: "" }, source: "featured", url: "", limit: 8, layout: "default", video_url: "", items: [] };
    if (newType === "category_cards") base.title = { ru: "Популярные категории", en: "Popular categories" };
    if (newType === "product_carousel") base.title = { ru: "Рекомендуем", en: "Featured products" };
    if (newType === "advantages") { base.title = { ru: "Почему выбирают нас", en: "Why shop with us" }; base.items = [
      { id: createId("adv"), title: { ru: "Быстрая доставка", en: "Fast delivery" }, text: { ru: "Удобные способы получения заказа", en: "Flexible delivery options" }, icon: "truck" },
      { id: createId("adv"), title: { ru: "Безопасная оплата", en: "Secure payment" }, text: { ru: "Надёжная обработка платежей", en: "Secure payment processing" }, icon: "shield" },
      { id: createId("adv"), title: { ru: "Поддержка", en: "Support" }, text: { ru: "Мы готовы помочь с заказом", en: "We are here to help" }, icon: "message" },
    ]; }
    if (newType === "faq") base.title = { ru: "Частые вопросы", en: "Frequently asked questions" };
    if (newType === "rich_text") base.title = { ru: "О магазине", en: "About the store" };
    if (newType === "promo_banner") base.title = { ru: "Специальное предложение", en: "Special offer" };
    if (newType === "video") base.title = { ru: "Видео", en: "Video" };
    updateHomepage((current) => ({ ...current, sections: [...current.sections, base] }));
  };
  const moveDragged = (targetIndex) => {
    if (dragIndex === null || dragIndex === targetIndex) return setDragIndex(null);
    updateHomepage((current) => {
      const next = [...current.sections]; const [item] = next.splice(dragIndex, 1); next.splice(targetIndex, 0, item); return { ...current, sections: next };
    });
    setDragIndex(null);
  };
  const addItem = (section) => setSection(section.id, { items: [...(section.items || []), { id: createId("item"), title: { ru: "", en: "" }, text: { ru: "", en: "" }, icon: section.type === "advantages" ? "sparkles" : "" }] });
  const setItem = (section, itemId, patch) => setSection(section.id, { items: (section.items || []).map((item) => item.id === itemId ? { ...item, ...patch } : item) });
  const removeItem = (section, itemId) => setSection(section.id, { items: (section.items || []).filter((item) => item.id !== itemId) });
  const typeLabel = (type) => ({ category_cards: t("shop_home_builder_type_categories"), product_carousel: t("shop_home_builder_type_products"), promo_banner: t("shop_home_builder_type_promo"), advantages: t("shop_home_builder_type_advantages"), faq: "FAQ", video: t("shop_home_builder_type_video"), rich_text: t("shop_home_builder_type_text") }[type] || type);
  const categoryImageGroups = useMemo(() => {
    const roots = Array.isArray(catalogTree) ? catalogTree : [];
    if (!roots.length) {
      return {
        primary: (categories || []).map((item) => ({ id: Number(item.id), name: item.name, path: "" })),
        subcategories: [],
      };
    }
    const legacyByCatalogId = new Map(
      (categories || [])
        .filter((item) => Number(item?.catalog_category_id || 0) > 0)
        .map((item) => [Number(item.catalog_category_id), item])
    );
    const primary = roots.map((item) => ({ id: Number(item.id), name: item.name, path: "" }));
    const subcategories = [];
    const walk = (items, parentPath = []) => {
      (items || []).forEach((item) => {
        const path = [...parentPath, item.name].filter(Boolean);
        if (Number(item.level || 0) > 0 || item.parent_id != null) {
          const legacy = legacyByCatalogId.get(Number(item.id));
          subcategories.push({
            id: Number(item.id),
            name: legacy?.name || item.name,
            path: parentPath.join(" → "),
          });
        }
        walk(item.children, path);
      });
    };
    walk(roots, []);
    return { primary, subcategories };
  }, [catalogTree, categories]);

  const renderCategoryImageCard = (category) => {
    const key = String(category.id);
    const image = homepage.category_images?.[key];
    const isBusy = busy === `category-${category.id}`;
    const progress = Math.max(0, Math.min(100, Number(uploadProgress[key] ?? 0)));
    return <article key={category.id} className={`${styles.categoryImageCard} ${isBusy ? styles.categoryImageCardBusy : ""}`}>
      <div className={styles.categoryImagePreview}>
        {image?.preview_url ? <img src={image.preview_url} alt={category.name} /> : <span>{String(category.name || "C").slice(0, 1).toUpperCase()}</span>}
        {isBusy ? <div className={styles.categoryUploadOverlay} aria-live="polite">
          <span className={styles.categoryUploadSpinner} aria-hidden="true" />
          <strong>{progress}%</strong>
        </div> : null}
      </div>
      <div className={styles.categoryImageInfo}>
        <strong>{category.name}</strong>
        {category.path ? <span className={styles.categoryImagePath}>{category.path}</span> : null}
        <small>{isBusy ? `${t("shop_home_category_image_loading")} · ${progress}%` : image?.attachment_id ? t("shop_home_category_image_custom") : t("shop_home_category_image_auto")}</small>
        {isBusy ? <div className={styles.categoryUploadProgressTrack}><span style={{ width: `${progress}%` }} /></div> : null}
      </div>
      <div className={styles.categoryImageActions}>
        <label className={`${styles.categoryUploadButton} ${isBusy ? styles.categoryUploadButtonBusy : ""}`} aria-disabled={isBusy}>
          <span className={styles.categoryUploadButtonIcon}>{isBusy ? <span className={styles.categoryUploadButtonSpinner} /> : <i className="lni lni-upload" />}</span>
          <span>{isBusy ? `${progress}%` : image?.attachment_id ? t("shop_home_category_image_replace") : t("shop_home_category_image_upload")}</span>
          <input type="file" accept="image/png,image/jpeg,image/webp" hidden disabled={isBusy} onChange={(e) => { const file = e.target.files?.[0]; if (file) uploadCategoryImage(category.id, file); e.target.value = ""; }} />
        </label>
        {image?.attachment_id ? <button type="button" className={styles.categoryDeleteButton} disabled={isBusy} onClick={() => removeCategoryImage(category.id)}><i className="lni lni-trash-can" aria-hidden="true" /><span>{t("delete")}</span></button> : null}
      </div>
    </article>;
  };

  return (
    <div className={styles.homepageEditor}>
      <SettingsCard title={t("shop_homepage_title")} subtitle={t("shop_homepage_hint")}>
        <div className={styles.homepageIntro}><ToggleCard label={t("shop_homepage_enabled")} hint={t("shop_homepage_enabled_hint")} checked={homepage.enabled} onChange={(checked) => updateHomepage((current) => ({ ...current, enabled: checked }))} /></div>
      </SettingsCard>

      <SettingsCard title={t("shop_template_title")} subtitle={t("shop_template_hint")}>
        <div className={styles.templateChooser}>
          {["modern", "marketplace"].map((template) => (
            <button
              type="button"
              key={template}
              className={`${styles.templateOption} ${homepage.template === template ? styles.templateOptionActive : ""}`}
              onClick={() => updateHomepage((current) => ({ ...current, template }))}
            >
              <span className={styles.templatePreview} data-template={template}>
                <i /><i /><i /><i />
              </span>
              <strong>{t(template === "modern" ? "shop_template_modern" : "shop_template_marketplace")}</strong>
              <small>{t(template === "modern" ? "shop_template_modern_hint" : "shop_template_marketplace_hint")}</small>
            </button>
          ))}
        </div>
      </SettingsCard>

      <SettingsCard title={t("shop_homepage_slider_title")} subtitle={t("shop_homepage_slider_hint")}>
        <div className={styles.editorToolbar}><span>{t("shop_homepage_slides_count", { count: homepage.slides.length })}</span><button type="button" className={styles.primaryButton} onClick={addSlide}>+ {t("shop_homepage_add_slide")}</button></div>
        <div className={styles.slideList}>
          {!homepage.slides.length ? <div className={styles.homepageEmpty}>{t("shop_homepage_no_slides")}</div> : null}
          {homepage.slides.map((slide, index) => <article className={styles.slideEditor} key={slide.id}>
            <div className={styles.slideNumber}>{index + 1}</div>
            <div className={styles.slideMediaColumn}><SlideUpload title={t("shop_homepage_desktop_image")} hint={t("shop_homepage_desktop_hint")} preview={slide.preview_url} busy={busy === `desktop-${slide.id}`} onFile={(file) => uploadSlide(slide, file, false)} t={t}/><SlideUpload title={t("shop_homepage_mobile_image")} hint={t("shop_homepage_mobile_hint")} preview={slide.mobile_preview_url} busy={busy === `mobile-${slide.id}`} onFile={(file) => uploadSlide(slide, file, true)} t={t} compact/></div>
            <div className={styles.slideFields}><div className={styles.localizedGrid}>
              <Field label={`${t("shop_homepage_slide_title")} · RU`}><input value={slide.title.ru} onChange={(e) => updateSlide(slide.id, { title: { ...slide.title, ru: e.target.value } })}/></Field><Field label={`${t("shop_homepage_slide_title")} · EN`}><input value={slide.title.en} onChange={(e) => updateSlide(slide.id, { title: { ...slide.title, en: e.target.value } })}/></Field>
              <Field label={`${t("shop_homepage_slide_text")} · RU`}><input value={slide.text.ru} onChange={(e) => updateSlide(slide.id, { text: { ...slide.text, ru: e.target.value } })}/></Field><Field label={`${t("shop_homepage_slide_text")} · EN`}><input value={slide.text.en} onChange={(e) => updateSlide(slide.id, { text: { ...slide.text, en: e.target.value } })}/></Field>
              <Field label={`${t("shop_homepage_button_text")} · RU`}><input value={slide.button.ru} onChange={(e) => updateSlide(slide.id, { button: { ...slide.button, ru: e.target.value } })}/></Field><Field label={`${t("shop_homepage_button_text")} · EN`}><input value={slide.button.en} onChange={(e) => updateSlide(slide.id, { button: { ...slide.button, en: e.target.value } })}/></Field>
            </div><div className={styles.slideLinkRow}><Field label={t("shop_homepage_link_type")}><select value={slide.link_type} onChange={(e) => updateSlide(slide.id, { link_type: e.target.value, target_id: null })}><option value="none">{t("shop_homepage_link_none")}</option><option value="category">{t("shop_homepage_link_category")}</option><option value="url">{t("shop_homepage_link_url")}</option></select></Field>{slide.link_type === "category" ? <Field label={t("shop_homepage_target_category")}><select value={slide.target_id || ""} onChange={(e) => updateSlide(slide.id, { target_id: Number(e.target.value) || null })}><option value="">—</option>{categories.map((category) => <option value={category.id} key={category.id}>{category.name}</option>)}</select></Field> : null}{slide.link_type === "url" ? <Field label={t("shop_homepage_target_url")}><input value={slide.url} onChange={(e) => updateSlide(slide.id, { url: e.target.value })} placeholder="https://..."/></Field> : null}</div></div>
            <div className={styles.slideActions}><label className={styles.miniToggle}><input type="checkbox" checked={slide.enabled} onChange={(e) => updateSlide(slide.id, { enabled: e.target.checked })}/><span>{t("shop_homepage_slide_enabled")}</span></label><button type="button" className={styles.dangerButton} disabled={busy === `remove-${slide.id}`} onClick={() => removeSlide(slide)}>{t("shop_homepage_delete_slide")}</button></div>
          </article>)}
        </div>
      </SettingsCard>

      <SettingsCard title={t("shop_homepage_menu_title")} subtitle={t("shop_homepage_menu_hint")}>
        <div className={styles.menuEditorHead}><ToggleCard label={t("shop_homepage_menu_enabled")} hint={t("shop_homepage_menu_enabled_hint")} checked={homepage.catalog_menu.enabled} onChange={(checked) => setCatalog({ enabled: checked })}/><Field label={t("shop_homepage_menu_mode")}><select value={homepage.catalog_menu.mode} onChange={(e) => setCatalog({ mode: e.target.value })}><option value="auto">{t("shop_homepage_menu_auto")}</option><option value="manual">{t("shop_homepage_menu_manual")}</option></select></Field></div>
        {homepage.catalog_menu.mode === "manual" ? <div className={styles.categoryPicker}>{categories.map((category) => <label key={category.id} className={homepage.catalog_menu.category_ids.includes(Number(category.id)) ? styles.categoryChoiceActive : styles.categoryChoice}><input type="checkbox" checked={homepage.catalog_menu.category_ids.includes(Number(category.id))} onChange={() => toggleCategory(Number(category.id))}/><span>{category.name}</span></label>)}</div> : <div className={styles.autoMenuNote}>{t("shop_homepage_menu_auto_hint")}</div>}
      </SettingsCard>

      <SettingsCard title={t("shop_home_builder_title")} subtitle={t("shop_home_builder_hint")}>
        <div className={styles.builderToolbar}>
          <Field label={t("shop_home_builder_new_block")}>
            <select value={newType} onChange={(e) => setNewType(e.target.value)}>
              <option value="category_cards">{t("shop_home_builder_type_categories")}</option>
              <option value="product_carousel">{t("shop_home_builder_type_products")}</option>
              <option value="promo_banner">{t("shop_home_builder_type_promo")}</option>
              <option value="advantages">{t("shop_home_builder_type_advantages")}</option>
              <option value="faq">FAQ</option>
              <option value="video">{t("shop_home_builder_type_video")}</option>
              <option value="rich_text">{t("shop_home_builder_type_text")}</option>
            </select>
          </Field>
          <button type="button" className={styles.primaryButton} onClick={addSection}>+ {t("shop_home_builder_add_block")}</button>
        </div>
        <div className={styles.sectionBuilderV2}>
          {homepage.sections.map((section, index) => (
            <article key={section.id} className={styles.builderCard} draggable onDragStart={() => setDragIndex(index)} onDragOver={(e) => e.preventDefault()} onDrop={() => moveDragged(index)}>
              <div className={styles.builderCardHead}>
                <span className={styles.dragHandle}>⋮⋮</span>
                <div className={styles.sectionMeta}><strong>{typeLabel(section.type)}</strong><small>{t("shop_home_builder_block")} #{index + 1}</small></div>
                <div className={styles.builderHeadActions}>
                  <button type="button" aria-label={t("move_up")} disabled={index === 0} onClick={() => moveSection(index,-1)}>↑</button>
                  <button type="button" aria-label={t("move_down")} disabled={index === homepage.sections.length-1} onClick={() => moveSection(index,1)}>↓</button>
                  <label className={styles.miniToggle}><input type="checkbox" checked={section.enabled} onChange={(e) => setSection(section.id,{enabled:e.target.checked})}/><span>{section.enabled ? t("enabled") : t("disabled")}</span></label>
                  <button type="button" className={styles.iconDanger} aria-label={t("delete")} onClick={() => removeSection(section.id)}>×</button>
                </div>
              </div>
              <div className={styles.builderFields}>
                <Field label={t("shop_home_builder_title_ru")}><input value={section.title?.ru || ""} onChange={(e) => setSection(section.id,{title:{...section.title,ru:e.target.value}})}/></Field>
                <Field label={t("shop_home_builder_title_en")}><input value={section.title?.en || ""} onChange={(e) => setSection(section.id,{title:{...section.title,en:e.target.value}})}/></Field>
                {section.type === "product_carousel" ? <>
                  <Field label={t("shop_home_builder_source")}><select value={section.source || "featured"} onChange={(e)=>setSection(section.id,{source:e.target.value})}><option value="featured">{t("shop_home_builder_featured")}</option><option value="catalog">{t("shop_home_builder_catalog")}</option></select></Field>
                  <Field label={t("shop_home_builder_limit")}><select value={section.limit || 8} onChange={(e)=>setSection(section.id,{limit:Number(e.target.value)})}><option value="4">4</option><option value="8">8</option><option value="12">12</option></select></Field>
                </> : null}
                {section.type === "category_cards" ? <>
                  <Field label={t("shop_home_builder_limit")}><select value={section.limit || 8} onChange={(e)=>setSection(section.id,{limit:Number(e.target.value)})}><option value="4">4</option><option value="8">8</option><option value="12">12</option></select></Field>
                  <div className={styles.categoryImageManager}>
                    <div className={styles.categoryImageManagerHead}>
                      <div><strong>{t("shop_home_category_images_title")}</strong><small>{t("shop_home_category_images_hint")}</small></div>
                    </div>
                    <div className={styles.categoryImageGroup}>
                      <div className={styles.categoryImageGroupHead}>
                        <strong>{t("shop_home_category_images_primary_title")}</strong>
                        <small>{t("shop_home_category_images_primary_hint", { count: Math.max(1, Number(section.limit || 8)) })}</small>
                      </div>
                      <div className={styles.categoryImageGrid}>
                        {categoryImageGroups.primary.length ? categoryImageGroups.primary.map(renderCategoryImageCard) : <div className={styles.homepageEmpty}>{t("shop_home_category_images_empty")}</div>}
                      </div>
                    </div>
                    <div className={styles.categoryImageGroup}>
                      <div className={styles.categoryImageGroupHead}>
                        <strong>{t("shop_home_category_images_subcategories_title")}</strong>
                        <small>{t("shop_home_category_images_subcategories_hint")}</small>
                      </div>
                      <div className={styles.categoryImageGrid}>
                        {categoryImageGroups.subcategories.length ? categoryImageGroups.subcategories.map(renderCategoryImageCard) : <div className={styles.homepageEmpty}>{t("shop_home_category_images_subcategories_empty")}</div>}
                      </div>
                    </div>
                  </div>
                </> : null}
                {section.type === "promo_banner" ? <>
                  <Field label={t("shop_home_builder_text_ru")}><textarea rows="2" value={section.text?.ru||""} onChange={(e)=>setSection(section.id,{text:{...section.text,ru:e.target.value}})}/></Field>
                  <Field label={t("shop_home_builder_text_en")}><textarea rows="2" value={section.text?.en||""} onChange={(e)=>setSection(section.id,{text:{...section.text,en:e.target.value}})}/></Field>
                  <Field label={t("shop_home_builder_button_ru")}><input value={section.button?.ru||""} onChange={(e)=>setSection(section.id,{button:{...section.button,ru:e.target.value}})}/></Field>
                  <Field label={t("shop_home_builder_button_en")}><input value={section.button?.en||""} onChange={(e)=>setSection(section.id,{button:{...section.button,en:e.target.value}})}/></Field>
                  <Field label={t("shop_home_builder_url")}><input value={section.url||""} onChange={(e)=>setSection(section.id,{url:e.target.value})}/></Field>
                </> : null}
                {section.type === "video" ? <>
                  <Field label={t("shop_home_builder_video_url")}><input value={section.video_url||""} onChange={(e)=>setSection(section.id,{video_url:e.target.value})}/></Field>
                  <Field label={t("shop_home_builder_description_ru")}><textarea rows="2" value={section.text?.ru||""} onChange={(e)=>setSection(section.id,{text:{...section.text,ru:e.target.value}})}/></Field>
                  <Field label={t("shop_home_builder_description_en")}><textarea rows="2" value={section.text?.en||""} onChange={(e)=>setSection(section.id,{text:{...section.text,en:e.target.value}})}/></Field>
                </> : null}
                {section.type === "rich_text" ? <>
                  <Field label={t("shop_home_builder_text_ru")}><textarea rows="3" value={section.text?.ru||""} onChange={(e)=>setSection(section.id,{text:{...section.text,ru:e.target.value}})}/></Field>
                  <Field label={t("shop_home_builder_text_en")}><textarea rows="3" value={section.text?.en||""} onChange={(e)=>setSection(section.id,{text:{...section.text,en:e.target.value}})}/></Field>
                </> : null}
              </div>
              {(section.type === "advantages" || section.type === "faq") ? (
                <div className={styles.builderItems}>
                  <div className={styles.builderItemsHead}><strong>{section.type === "faq" ? t("shop_home_builder_questions") : t("shop_home_builder_cards")}</strong><button type="button" onClick={()=>addItem(section)}>+ {t("shop_home_builder_add")}</button></div>
                  {(section.items||[]).map((item)=><div className={styles.builderItem} key={item.id}><div className={styles.builderItemFields}><input placeholder={t("shop_home_builder_ru_title_placeholder")} value={item.title?.ru||""} onChange={(e)=>setItem(section,item.id,{title:{...item.title,ru:e.target.value}})}/><input placeholder={t("shop_home_builder_en_title_placeholder")} value={item.title?.en||""} onChange={(e)=>setItem(section,item.id,{title:{...item.title,en:e.target.value}})}/><textarea rows="2" placeholder={t("shop_home_builder_ru_text_placeholder")} value={item.text?.ru||""} onChange={(e)=>setItem(section,item.id,{text:{...item.text,ru:e.target.value}})}/><textarea rows="2" placeholder={t("shop_home_builder_en_text_placeholder")} value={item.text?.en||""} onChange={(e)=>setItem(section,item.id,{text:{...item.text,en:e.target.value}})}/></div><button type="button" className={styles.iconDanger} aria-label={t("delete")} onClick={()=>removeItem(section,item.id)}>×</button></div>)}
                </div>
              ) : null}
            </article>
          ))}
        </div>
      </SettingsCard>
    </div>
  );
}

function SlideUpload({ title, hint, preview, busy, onFile, t, compact = false }) {
  const inputRef = useRef(null);
  return <div className={`${styles.slideUpload} ${compact ? styles.slideUploadCompact : ""}`} onClick={() => !busy && inputRef.current?.click()} role="button" tabIndex={0} onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !busy) inputRef.current?.click(); }}><input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ""; }} />{preview ? <img src={preview} alt="" /> : <div className={styles.slideUploadPlaceholder}><i className="lni lni-image" /><strong>{busy ? t("shop_homepage_uploading") : title}</strong><small>{hint}</small></div>}<span className={styles.slideUploadBadge}>{busy ? t("shop_homepage_uploading") : t("shop_homepage_replace_image")}</span></div>;
}

function ContactsAndLocationsPanel({
  contact,
  locations,
  updateContactField,
  updateContactLocalizedField,
  addContactExtraField,
  updateContactExtraField,
  removeContactExtraField,
  addServiceLocation,
  updateServiceLocation,
  removeServiceLocation,
  addLocationExtraField,
  updateLocationExtraField,
  removeLocationExtraField,
  t,
}) {
  const [language, setLanguage] = useState("ru");

  return (
    <div className={styles.contactSettingsStack}>
      <SettingsCard title={t("shop_settings_contacts_title")} subtitle={t("shop_settings_contacts_hint")}>
        <div className={styles.sectionToolbar}>
          <div>
            <strong>{t("shop_settings_contact_static_fields")}</strong>
            <span>{t("shop_settings_contact_static_fields_hint")}</span>
          </div>
          <LanguageTabs value={language} onChange={setLanguage} />
        </div>

        <div className={styles.formGrid}>
          <Field label={t("shop_settings_legal_name")}>
            <input value={contact.legal_name} onChange={(e) => updateContactField("legal_name", e.target.value)} />
          </Field>
          <Field label={t("shop_settings_registry_code")}>
            <input value={contact.registry_code} onChange={(e) => updateContactField("registry_code", e.target.value)} />
          </Field>
          <Field label={t("shop_settings_vat_number")}>
            <input value={contact.vat_number} onChange={(e) => updateContactField("vat_number", e.target.value)} />
          </Field>
          <Field label={t("shop_settings_support_email")}>
            <input type="email" value={contact.email} onChange={(e) => updateContactField("email", e.target.value)} />
          </Field>
          <Field label={t("shop_settings_support_phone")}>
            <input value={contact.phone} onChange={(e) => updateContactField("phone", e.target.value)} />
          </Field>
          <Field label={t("shop_settings_city")}>
            <input value={contact.city} onChange={(e) => updateContactField("city", e.target.value)} />
          </Field>
          <Field label={t("shop_settings_postal_code")}>
            <input value={contact.postal_code} onChange={(e) => updateContactField("postal_code", e.target.value)} />
          </Field>
          <Field label={t("shop_settings_address")}>
            <input value={contact.address} onChange={(e) => updateContactField("address", e.target.value)} />
          </Field>
          <Field label={`${t("shop_settings_working_hours")} · ${language.toUpperCase()}`} className={styles.fullWidth}>
            <textarea
              rows="3"
              value={contact.working_hours?.[language] || ""}
              onChange={(e) => updateContactLocalizedField("working_hours", language, e.target.value)}
              placeholder={t("shop_settings_working_hours_placeholder")}
            />
          </Field>
        </div>

        <DynamicFieldsEditor
          fields={contact.extra_fields}
          language={language}
          onAdd={addContactExtraField}
          onUpdate={(id, part, lang, value) => updateContactExtraField(id, part, lang, value)}
          onRemove={removeContactExtraField}
          t={t}
        />
      </SettingsCard>

      <SettingsCard title={t("shop_settings_locations_title")} subtitle={t("shop_settings_locations_hint")}>
        <div className={styles.locationHeader}>
          <div className={styles.locationHeaderCopy}>
            <span className={styles.locationIcon}>⌖</span>
            <div>
              <strong>{t("shop_settings_locations_count", { count: locations.length })}</strong>
              <small>{t("shop_settings_locations_map_hint")}</small>
            </div>
          </div>
          <button className={styles.primaryButton} type="button" onClick={addServiceLocation}>
            + {t("shop_settings_add_location")}
          </button>
        </div>

        {locations.length === 0 ? (
          <div className={styles.locationEmpty}>
            <span>⌖</span>
            <strong>{t("shop_settings_no_locations")}</strong>
            <p>{t("shop_settings_no_locations_hint")}</p>
            <button className={styles.secondaryButton} type="button" onClick={addServiceLocation}>
              + {t("shop_settings_add_location")}
            </button>
          </div>
        ) : (
          <div className={styles.locationList}>
            {locations.map((location, index) => (
              <LocationEditor
                key={location.id}
                location={location}
                index={index}
                language={language}
                onLanguageChange={setLanguage}
                onUpdate={updateServiceLocation}
                onRemove={removeServiceLocation}
                onAddExtra={addLocationExtraField}
                onUpdateExtra={updateLocationExtraField}
                onRemoveExtra={removeLocationExtraField}
                t={t}
              />
            ))}
          </div>
        )}
      </SettingsCard>
    </div>
  );
}

function LanguageTabs({ value, onChange }) {
  return (
    <div className={styles.languageTabs}>
      <button type="button" className={value === "ru" ? styles.active : ""} onClick={() => onChange("ru")}>RU</button>
      <button type="button" className={value === "en" ? styles.active : ""} onClick={() => onChange("en")}>EN</button>
    </div>
  );
}

function DynamicFieldsEditor({ fields, language, onAdd, onUpdate, onRemove, t }) {
  return (
    <div className={styles.dynamicFields}>
      <div className={styles.dynamicFieldsHead}>
        <div>
          <strong>{t("shop_settings_custom_contact_fields")}</strong>
          <span>{t("shop_settings_custom_contact_fields_hint")}</span>
        </div>
        <button type="button" className={styles.secondaryButton} onClick={onAdd}>+ {t("shop_settings_add_field")}</button>
      </div>
      {fields.length > 0 && (
        <div className={styles.dynamicFieldList}>
          {fields.map((field) => (
            <div className={styles.dynamicFieldRow} key={field.id}>
              <input
                value={field.label?.[language] || ""}
                onChange={(e) => onUpdate(field.id, "label", language, e.target.value)}
                placeholder={`${t("shop_settings_field_label")} · ${language.toUpperCase()}`}
              />
              <input
                value={field.value?.[language] || ""}
                onChange={(e) => onUpdate(field.id, "value", language, e.target.value)}
                placeholder={`${t("shop_settings_field_value")} · ${language.toUpperCase()}`}
              />
              <button type="button" className={styles.dangerIconButton} onClick={() => onRemove(field.id)} aria-label={t("delete")}>×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const LEAFLET_JS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
const LEAFLET_CSS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
let leafletLoaderPromise = null;

function loadLeaflet() {
  if (typeof window === "undefined") return Promise.reject(new Error("Leaflet requires a browser"));
  if (window.L) return Promise.resolve(window.L);
  if (leafletLoaderPromise) return leafletLoaderPromise;

  leafletLoaderPromise = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = LEAFLET_CSS;
      link.crossOrigin = "";
      document.head.appendChild(link);
    }

    const existing = document.querySelector(`script[src="${LEAFLET_JS}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.L), { once: true });
      existing.addEventListener("error", reject, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = LEAFLET_JS;
    script.async = true;
    script.crossOrigin = "";
    script.onload = () => resolve(window.L);
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return leafletLoaderPromise;
}

function InteractiveLocationMap({ location, language, onUpdate, t }) {
  const mapElementRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const onUpdateRef = useRef(onUpdate);
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searching, setSearching] = useState(false);

  onUpdateRef.current = onUpdate;

  const lat = Number(location.latitude);
  const lng = Number(location.longitude);
  const hasPoint =
    location.latitude !== "" &&
    location.longitude !== "" &&
    Number.isFinite(lat) &&
    Number.isFinite(lng);

  const setCoordinates = (nextLat, nextLng) => {
    if (!Number.isFinite(nextLat) || !Number.isFinite(nextLng)) return;
    const safeLat = Math.max(-90, Math.min(90, nextLat));
    const safeLng = Math.max(-180, Math.min(180, nextLng));
    onUpdateRef.current(location.id, "latitude", safeLat.toFixed(6));
    onUpdateRef.current(location.id, "longitude", safeLng.toFixed(6));
  };

  useEffect(() => {
    let cancelled = false;
    let resizeObserver = null;

    loadLeaflet()
      .then((L) => {
        if (cancelled || !mapElementRef.current || mapRef.current) return;

        const initialCenter = hasPoint ? [lat, lng] : [59.437, 24.7536];
        const map = L.map(mapElementRef.current, {
          zoomControl: true,
          attributionControl: true,
          scrollWheelZoom: true,
        }).setView(initialCenter, hasPoint ? 15 : 7);

        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          maxZoom: 19,
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        }).addTo(map);

        map.on("click", (event) => {
          setCoordinates(event.latlng.lat, event.latlng.lng);
        });

        mapRef.current = map;
        setMapReady(true);

        if (typeof ResizeObserver !== "undefined") {
          resizeObserver = new ResizeObserver(() => map.invalidateSize());
          resizeObserver.observe(mapElementRef.current);
        }

        window.setTimeout(() => map.invalidateSize(), 80);
      })
      .catch(() => {
        if (!cancelled) setMapError(t("shop_settings_map_load_error"));
      });

    return () => {
      cancelled = true;
      if (resizeObserver) resizeObserver.disconnect();
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
      markerRef.current = null;
    };
    // The map is initialized once for this location editor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.id]);

  useEffect(() => {
    if (!mapReady || !mapRef.current || !window.L) return;
    const L = window.L;

    if (!hasPoint) {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      return;
    }

    const point = [lat, lng];
    if (!markerRef.current) {
      const icon = L.divIcon({
        className: styles.crmMapMarkerHost,
        html: '<span class="crm-map-pin"><span></span></span>',
        iconSize: [36, 44],
        iconAnchor: [18, 42],
      });
      const marker = L.marker(point, { draggable: true, icon }).addTo(mapRef.current);
      marker.on("dragend", () => {
        const position = marker.getLatLng();
        setCoordinates(position.lat, position.lng);
      });
      markerRef.current = marker;
      mapRef.current.setView(point, Math.max(mapRef.current.getZoom(), 15), { animate: true });
    } else {
      markerRef.current.setLatLng(point);
    }
  }, [hasPoint, lat, lng, mapReady]);

  const findAddress = async (queryOverride = "") => {
    const fallbackQuery = [location.address?.[language], location.city?.[language]]
      .filter(Boolean)
      .join(", ")
      .trim();
    const query = (queryOverride || searchText || fallbackQuery).trim();
    if (!query) return;

    setSearching(true);
    setMapError("");
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&q=${encodeURIComponent(query)}`,
        { headers: { "Accept-Language": language === "ru" ? "ru" : "en" } }
      );
      if (!response.ok) throw new Error("Search failed");
      const results = await response.json();
      if (!Array.isArray(results) || !results.length) {
        setMapError(t("shop_settings_map_search_not_found"));
        return;
      }
      const nextLat = Number(results[0].lat);
      const nextLng = Number(results[0].lon);
      setCoordinates(nextLat, nextLng);
      mapRef.current?.setView([nextLat, nextLng], 16, { animate: true });
      setSearchText(results[0].display_name || query);
    } catch {
      setMapError(t("shop_settings_map_search_error"));
    } finally {
      setSearching(false);
    }
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      setMapError(t("shop_settings_map_geolocation_error"));
      return;
    }
    setMapError("");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const nextLat = position.coords.latitude;
        const nextLng = position.coords.longitude;
        setCoordinates(nextLat, nextLng);
        mapRef.current?.setView([nextLat, nextLng], 16, { animate: true });
      },
      () => setMapError(t("shop_settings_map_geolocation_error")),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const clearPoint = () => {
    onUpdate(location.id, "latitude", "");
    onUpdate(location.id, "longitude", "");
    if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }
  };

  return (
    <div className={styles.locationMapCard}>
      <div className={styles.mapToolbar}>
        <div className={styles.mapSearchBox}>
          <span className={styles.mapSearchIcon}>⌕</span>
          <input
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                findAddress();
              }
            }}
            placeholder={t("shop_settings_map_search_placeholder")}
          />
          <button type="button" onClick={() => findAddress()} disabled={searching}>
            {searching ? "…" : t("shop_settings_map_search")}
          </button>
        </div>
        <div className={styles.mapToolbarActions}>
          <button type="button" className={styles.mapSecondaryButton} onClick={() => findAddress([location.address?.[language], location.city?.[language]].filter(Boolean).join(", "))}>
            {t("shop_settings_map_use_address")}
          </button>
          <button type="button" className={styles.mapSecondaryButton} onClick={useCurrentLocation}>
            ◎ {t("shop_settings_map_my_location")}
          </button>
        </div>
      </div>

      <div className={styles.interactiveMapWrap}>
        <div ref={mapElementRef} className={styles.interactiveMap} />
        {!mapReady && !mapError && (
          <div className={styles.mapLoadingOverlay}>
            <span className={styles.mapLoadingDot} />
            {t("shop_settings_map_loading")}
          </div>
        )}
        <div className={styles.mapInstructionBadge}>
          <span>⌖</span>
          <div>
            <strong>{hasPoint ? t("shop_settings_map_point_selected") : t("shop_settings_map_choose_point")}</strong>
            <small>{t("shop_settings_map_click_hint")}</small>
          </div>
        </div>
      </div>

      <div className={styles.mapFooter}>
        <div className={styles.mapCoordinatesSummary}>
          <span className={hasPoint ? styles.mapStatusReady : styles.mapStatusPending} />
          <div>
            <strong>{hasPoint ? `${lat.toFixed(6)}, ${lng.toFixed(6)}` : t("shop_settings_location_no_point")}</strong>
            <small>{t("shop_settings_map_drag_hint")}</small>
          </div>
        </div>
        {hasPoint && (
          <button type="button" className={styles.mapClearButton} onClick={clearPoint}>
            {t("shop_settings_map_clear_point")}
          </button>
        )}
      </div>

      {mapError && <div className={styles.mapErrorMessage}>{mapError}</div>}
    </div>
  );
}

function LocationEditor({
  location,
  index,
  language,
  onLanguageChange,
  onUpdate,
  onRemove,
  onAddExtra,
  onUpdateExtra,
  onRemoveExtra,
  t,
}) {
  const lat = Number(location.latitude);
  const lng = Number(location.longitude);
  const hasMap = Number.isFinite(lat) && Number.isFinite(lng) && location.latitude !== "" && location.longitude !== "";

  return (
    <article className={styles.locationEditor}>
      <div className={styles.locationEditorHead}>
        <div className={styles.locationIndex}>{index + 1}</div>
        <div className={styles.locationEditorTitle}>
          <strong>{location.name?.[language] || location.city?.[language] || t("shop_settings_location_untitled")}</strong>
          <span>{hasMap ? `${lat.toFixed(5)}, ${lng.toFixed(5)}` : t("shop_settings_location_no_point")}</span>
        </div>
        <LanguageTabs value={language} onChange={onLanguageChange} />
        <button type="button" className={styles.dangerButton} onClick={() => onRemove(location.id)}>{t("delete")}</button>
      </div>

      <div className={styles.locationEditorGrid}>
        <div className={styles.locationFields}>
          <div className={styles.formGrid}>
            <Field label={`${t("shop_settings_location_name")} · ${language.toUpperCase()}`}>
              <input value={location.name?.[language] || ""} onChange={(e) => onUpdate(location.id, "name", e.target.value, language)} />
            </Field>
            <Field label={`${t("shop_settings_city")} · ${language.toUpperCase()}`}>
              <input value={location.city?.[language] || ""} onChange={(e) => onUpdate(location.id, "city", e.target.value, language)} />
            </Field>
            <Field label={`${t("shop_settings_address")} · ${language.toUpperCase()}`} className={styles.fullWidth}>
              <input value={location.address?.[language] || ""} onChange={(e) => onUpdate(location.id, "address", e.target.value, language)} />
            </Field>
            <Field label={t("shop_settings_support_phone")}>
              <input value={location.phone} onChange={(e) => onUpdate(location.id, "phone", e.target.value)} />
            </Field>
            <Field label={t("shop_settings_support_email")}>
              <input type="email" value={location.email} onChange={(e) => onUpdate(location.id, "email", e.target.value)} />
            </Field>
            <Field label={t("shop_settings_latitude")}>
              <input inputMode="decimal" placeholder="59.4370" value={location.latitude ?? ""} onChange={(e) => onUpdate(location.id, "latitude", e.target.value)} />
            </Field>
            <Field label={t("shop_settings_longitude")}>
              <input inputMode="decimal" placeholder="24.7536" value={location.longitude ?? ""} onChange={(e) => onUpdate(location.id, "longitude", e.target.value)} />
            </Field>
            <Field label={`${t("shop_settings_working_hours")} · ${language.toUpperCase()}`} className={styles.fullWidth}>
              <textarea rows="3" value={location.working_hours?.[language] || ""} onChange={(e) => onUpdate(location.id, "working_hours", e.target.value, language)} />
            </Field>
          </div>

          <DynamicFieldsEditor
            fields={location.extra_fields}
            language={language}
            onAdd={() => onAddExtra(location.id)}
            onUpdate={(fieldId, part, lang, value) => onUpdateExtra(location.id, fieldId, part, lang, value)}
            onRemove={(fieldId) => onRemoveExtra(location.id, fieldId)}
            t={t}
          />
        </div>

        <InteractiveLocationMap
          location={location}
          language={language}
          onUpdate={onUpdate}
          t={t}
        />
      </div>
    </article>
  );
}

function BrandingPanel({ form, homepage, crmAssets, previewLogo, previewFavicon, assetBusy, setField, setThemeField, setAutoTextContrast, uploadAsset, removeAsset, resetTheme, t }) {
  return (
    <div className={styles.brandingGrid}>
      <div className={styles.brandingControls}>
        <SettingsCard title={t("shop_settings_branding_title")} subtitle={t("shop_settings_branding_hint")}>
          <ToggleCard
            label={t("shop_settings_use_crm_assets")}
            hint={t("shop_settings_use_crm_assets_hint")}
            checked={form.use_crm_assets}
            onChange={(checked) => setField("use_crm_assets", checked)}
          />

          <div className={styles.assetsBox}>
            {form.use_crm_assets ? (
              <div className={styles.inheritedAssets}>
                <AssetPreview title={t("shop_settings_logo")} url={crmAssets.logo_url} fallback="V" />
                <AssetPreview title={t("shop_settings_favicon")} url={crmAssets.favicon_url} fallback="V" small />
                <p>{t("shop_settings_inherited_assets_note")}</p>
              </div>
            ) : (
              <>
                <UploadRow
                  title={t("shop_settings_logo")}
                  hint={t("shop_settings_logo_hint")}
                  accept="image/png,image/jpeg,image/webp"
                  url={form.logo_url}
                  busy={assetBusy === "logo"}
                  onPick={(file) => uploadAsset("logo", file)}
                  onRemove={() => removeAsset("logo")}
                  t={t}
                />
                <UploadRow
                  title={t("shop_settings_favicon")}
                  hint={t("shop_settings_favicon_hint")}
                  accept="image/x-icon,image/png,image/jpeg,image/webp"
                  url={form.favicon_url}
                  busy={assetBusy === "favicon"}
                  onPick={(file) => uploadAsset("favicon", file)}
                  onRemove={() => removeAsset("favicon")}
                  t={t}
                />
              </>
            )}
          </div>
        </SettingsCard>

        <SettingsCard title={t("shop_typography_title")} subtitle={t("shop_typography_hint")}>
          <div className={styles.typographyStandalone}>
            <div className={styles.typographyToolbar}>
              <span>{t("shop_typography_preview_hint")}</span>
              <button type="button" className={styles.textButton} onClick={() => { setField("body_font_scale", 100); setField("heading_font_scale", 100); }}>
                {t("shop_typography_reset")}
              </button>
            </div>
            <div className={styles.typographyPresets}>
              {[
                { key: "compact", body: 90, heading: 94 },
                { key: "standard", body: 100, heading: 100 },
                { key: "large", body: 112, heading: 116 },
              ].map((preset) => {
                const active = Number(form.body_font_scale) === preset.body && Number(form.heading_font_scale) === preset.heading;
                return (
                  <button
                    key={preset.key}
                    type="button"
                    className={`${styles.typographyPreset} ${active ? styles.typographyPresetActive : ""}`}
                    onClick={() => { setField("body_font_scale", preset.body); setField("heading_font_scale", preset.heading); }}
                  >
                    <strong>{t(`shop_typography_${preset.key}`)}</strong>
                    <span>{preset.body}% / {preset.heading}%</span>
                  </button>
                );
              })}
            </div>
            <div className={styles.typographySliders}>
              <label>
                <span><b>{t("shop_typography_body")}</b><em>{Math.round(Number(form.body_font_scale || 100))}%</em></span>
                <input type="range" min="85" max="120" step="1" value={form.body_font_scale} onChange={(e) => setField("body_font_scale", Number(e.target.value))} />
                <small>{t("shop_typography_body_hint")}</small>
              </label>
              <label>
                <span><b>{t("shop_typography_headings")}</b><em>{Math.round(Number(form.heading_font_scale || 100))}%</em></span>
                <input type="range" min="90" max="130" step="1" value={form.heading_font_scale} onChange={(e) => setField("heading_font_scale", Number(e.target.value))} />
                <small>{t("shop_typography_headings_hint")}</small>
              </label>
            </div>
          </div>
        </SettingsCard>

        <SettingsCard title={t("shop_settings_light_theme")} subtitle={t("shop_settings_light_theme_hint")}>
          <div className={styles.themeHead}>
            <div />
            <button type="button" className={styles.textButton} onClick={resetTheme}>{t("shop_settings_theme_default")}</button>
          </div>
          <div className={styles.autoContrastCard}>
            <div className={styles.autoContrastCopy}>
              <strong>{t("shop_ui_automatic_text_contrast")}</strong>
              <small>{t("shop_ui_when_a_background_changes_crm_automatically_chooses_readable_text_mute")}</small>
            </div>
            <label className={styles.smartSwitch}>
              <input type="checkbox" checked={form.auto_text_contrast} onChange={(e) => setAutoTextContrast(e.target.checked)} />
              <span />
            </label>
          </div>


          <div className={styles.colorGrid}>
            <ColorField label={t("shop_settings_primary_color")} value={form.primary_color} onChange={(value) => setField("primary_color", value)} />
            <ColorField label={t("shop_settings_page_background")} value={form.page_background} onChange={(value) => setThemeField("page_background", value)} />
            <ColorField label={t("shop_settings_surface_color")} value={form.surface_color} onChange={(value) => setThemeField("surface_color", value)} />
            <ColorField label={t("shop_settings_header_background")} value={form.header_background} onChange={(value) => setThemeField("header_background", value)} />
            <ColorField label={t("shop_settings_text_color")} value={form.text_color} onChange={(value) => setField("text_color", value)} disabled={form.auto_text_contrast} />
            <ColorField label={t("shop_settings_muted_color")} value={form.muted_color} onChange={(value) => setField("muted_color", value)} disabled={form.auto_text_contrast} />
            <ColorField label={t("shop_settings_border_color")} value={form.border_color} onChange={(value) => setField("border_color", value)} disabled={form.auto_text_contrast} />
          </div>
          <div className={styles.darkThemeNote}><i className="lni lni-moon-half-right-5" /><span>{t("shop_settings_dark_theme_note")}</span></div>
        </SettingsCard>
      </div>

      <StorePreview form={form} homepage={homepage} logo={previewLogo} favicon={previewFavicon} t={t} />
    </div>
  );
}

function StorePreview({ form, homepage, logo, favicon, t }) {
  const iframeRef = useRef(null);
  const [frameReady, setFrameReady] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const previewUrl = useMemo(() => {
    const configured = String(form.public_url || "").trim();
    if (configured && /^https?:\/\//i.test(configured)) return configured;
    if (typeof window !== "undefined" && ["localhost", "127.0.0.1"].includes(window.location.hostname)) return "http://localhost:5173";
    return configured || "/";
  }, [form.public_url]);

  const previewPayload = useMemo(() => ({
    type: "VELSHOP_THEME_PREVIEW",
    payload: {
      primary_color: form.primary_color,
      page_background: form.page_background,
      surface_color: form.surface_color,
      header_background: form.header_background,
      text_color: form.text_color,
      muted_color: form.muted_color,
      border_color: form.border_color,
      body_font_scale: Number(form.body_font_scale || 100),
      heading_font_scale: Number(form.heading_font_scale || 100),
      store_name: form.store_name,
      logo_url: logo || "",
      favicon_url: favicon || "",
      force_theme: "light",
      homepage_template: homepage?.template || "modern",
    },
  }), [form.primary_color, form.page_background, form.surface_color, form.header_background, form.text_color, form.muted_color, form.border_color, form.body_font_scale, form.heading_font_scale, form.store_name, logo, favicon, homepage?.template]);

  useEffect(() => {
    if (!frameReady || !iframeRef.current?.contentWindow) return undefined;
    const send = () => iframeRef.current?.contentWindow?.postMessage(previewPayload, "*");
    send();
    const shortTimer = window.setTimeout(send, 60);
    const settleTimer = window.setTimeout(send, 220);
    return () => { window.clearTimeout(shortTimer); window.clearTimeout(settleTimer); };
  }, [frameReady, previewPayload]);

  return (
    <div className={styles.previewColumn}>
      <div className={styles.previewLabel}>
        <div><strong>{t("shop_settings_live_preview")}</strong><span>{t("shop_settings_live_preview_real_hint")}</span></div>
        <span className={styles.previewLiveBadge}>{frameReady ? t("shop_preview_live") : t("shop_preview_connecting")}</span>
      </div>
      <div className={styles.liveBrowserPreview}>
        <div className={styles.browserChrome}>
          <div className={styles.browserTab}>{favicon ? <img src={favicon} alt="" /> : <span>V</span>}<b>{form.store_name || "VelShop"}</b><i>×</i></div>
          <div className={styles.previewAddress}>{previewUrl}</div>
          <div className={styles.browserDots}><span /><span /><span /></div>
        </div>
        {previewError ? (
          <div className={styles.previewUnavailable}>
            <strong>{t("shop_preview_unavailable")}</strong>
            <span>{t("shop_preview_unavailable_hint")}</span>
          </div>
        ) : (
          <iframe
            ref={iframeRef}
            title={t("shop_settings_live_preview")}
            src={previewUrl}
            className={styles.liveStoreFrame}
            onLoad={() => { setFrameReady(true); setPreviewError(false); }}
            onError={() => setPreviewError(true)}
          />
        )}
      </div>
    </div>
  );
}


function AuctionPanel({ form, setForm, shopText, currencySymbol }) {
  const { t } = useTranslation();
  const [lots, setLots] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingLots, setLoadingLots] = useState(false);
  const [actionError, setActionError] = useState("");
  const [draft, setDraft] = useState({ product_id: "", start_price: "", min_increment: form.auction?.default_increment || 1, reserve_price: "", buy_now_price: "", starts_at: "", ends_at: "", status: "scheduled" });

  const auctionEnabled = Boolean(form.commerce_modules?.auction);
  const updateAuction = (key, value) => setForm((current) => ({ ...current, auction: { ...current.auction, [key]: value } }));
  const load = async () => {
    if (!auctionEnabled) return;
    setLoadingLots(true); setActionError("");
    try {
      const [lotsResponse, productsResponse] = await Promise.all([
        api.get("/auth/auction/lots"),
        api.get("/public/shop/products", { params: { limit: 100, sort_by: "newest" } }),
      ]);
      setLots(Array.isArray(lotsResponse.data) ? lotsResponse.data : []);
      setProducts(Array.isArray(productsResponse.data?.items) ? productsResponse.data.items : []);
    } catch (error) {
      setActionError(error?.response?.data?.detail || t("shop_ui_could_not_load_auction"));
    } finally { setLoadingLots(false); }
  };
  useEffect(() => { load(); }, [auctionEnabled]);

  const localIso = (value) => value ? new Date(value).toISOString() : null;
  const createLot = async () => {
    if (!draft.product_id || !draft.starts_at || !draft.ends_at || draft.start_price === "") {
      setActionError(t("shop_ui_choose_a_product_start_price_start_and_end_time")); return;
    }
    setActionError("");
    try {
      await api.post("/auth/auction/lots", {
        product_id: Number(draft.product_id), start_price: Number(draft.start_price), min_increment: Number(draft.min_increment || 1),
        reserve_price: draft.reserve_price === "" ? null : Number(draft.reserve_price), buy_now_price: draft.buy_now_price === "" ? null : Number(draft.buy_now_price),
        starts_at: localIso(draft.starts_at), ends_at: localIso(draft.ends_at), status: draft.status,
      });
      setDraft({ product_id: "", start_price: "", min_increment: form.auction?.default_increment || 1, reserve_price: "", buy_now_price: "", starts_at: "", ends_at: "", status: "scheduled" });
      await load();
    } catch (error) { setActionError(error?.response?.data?.detail || t("shop_ui_could_not_create_lot")); }
  };
  const setLotStatus = async (lot, status) => {
    try { await api.patch(`/auth/auction/lots/${lot.id}`, { status }); await load(); }
    catch (error) { setActionError(error?.response?.data?.detail || t("shop_ui_could_not_update_lot")); }
  };
  const deleteLot = async (lot) => {
    if (!window.confirm(t("shop_ui_delete_this_lot"))) return;
    try { await api.delete(`/auth/auction/lots/${lot.id}`); await load(); }
    catch (error) { setActionError(error?.response?.data?.detail || t("shop_ui_a_lot_with_bids_cannot_be_deleted_cancel_it_instead")); }
  };

  return <div className={styles.auctionStack}>
    <SettingsCard title={t("shop_ui_auction")} subtitle={t("shop_ui_auction_rules_apply_to_the_whole_storefront_lots_are_created_from_exis")}>
      {!auctionEnabled ? <div className={styles.auctionDisabledNotice}>{t("shop_ui_auction_is_disabled_enable_the_auction_module_in_general")}</div> : null}
      <div className={styles.formGrid}>
        <ToggleCard label={t("shop_ui_show_bid_count")} hint={t("shop_ui_customers_can_see_lot_activity")} checked={form.auction?.show_bid_count !== false} onChange={(v) => updateAuction("show_bid_count", v)} />
        <ToggleCard label={t("shop_ui_show_bid_history")} hint={t("shop_ui_bidder_identities_stay_masked")} checked={form.auction?.show_bid_history !== false} onChange={(v) => updateAuction("show_bid_history", v)} />
        <ToggleCard label={t("shop_ui_anti_sniping")} hint={t("shop_ui_a_late_bid_automatically_extends_the_auction")} checked={form.auction?.anti_sniping_enabled !== false} onChange={(v) => updateAuction("anti_sniping_enabled", v)} />
        <Field label={t("shop_ui_extension_minutes")}><input type="number" min="0" max="30" value={form.auction?.anti_sniping_minutes ?? 2} onChange={(e) => updateAuction("anti_sniping_minutes", Number(e.target.value || 0))} /></Field>
        <Field label={t("shop_ui_default_bid_increment")}><div className={styles.moneyInput}><span>{currencySymbol}</span><input type="number" min="0.01" step="0.01" value={form.auction?.default_increment ?? 1} onChange={(e) => updateAuction("default_increment", Number(e.target.value || 1))} /></div></Field>
      </div>
    </SettingsCard>
    {auctionEnabled ? <SettingsCard title={t("shop_ui_create_auction_lot")} subtitle={t("shop_ui_the_product_stays_in_crm_the_auction_stores_only_bidding_rules_and_bid")}>
      <div className={styles.formGrid}>
        <Field label={t("shop_ui_product")} required><select value={draft.product_id} onChange={(e) => setDraft((x) => ({...x, product_id:e.target.value}))}><option value="">—</option>{products.map((p)=><option key={p.id} value={p.id}>{p.name} · {p.price} {currencySymbol}</option>)}</select></Field>
        <Field label={t("shop_ui_starting_price")} required><div className={styles.moneyInput}><span>{currencySymbol}</span><input type="number" min="0" step="0.01" value={draft.start_price} onChange={(e)=>setDraft((x)=>({...x,start_price:e.target.value}))}/></div></Field>
        <Field label={t("shop_ui_minimum_increment")}><div className={styles.moneyInput}><span>{currencySymbol}</span><input type="number" min="0.01" step="0.01" value={draft.min_increment} onChange={(e)=>setDraft((x)=>({...x,min_increment:e.target.value}))}/></div></Field>
        <Field label={t("shop_ui_reserve_price")} hint={t("shop_ui_optional_the_auction_is_successful_only_when_reserve_is_met")}><div className={styles.moneyInput}><span>{currencySymbol}</span><input type="number" min="0" step="0.01" value={draft.reserve_price} onChange={(e)=>setDraft((x)=>({...x,reserve_price:e.target.value}))}/></div></Field>
        <Field label={t("shop_ui_buy_now")}><div className={styles.moneyInput}><span>{currencySymbol}</span><input type="number" min="0" step="0.01" value={draft.buy_now_price} onChange={(e)=>setDraft((x)=>({...x,buy_now_price:e.target.value}))}/></div></Field>
        <Field label={t("shop_ui_starts")} required><input type="datetime-local" value={draft.starts_at} onChange={(e)=>setDraft((x)=>({...x,starts_at:e.target.value}))}/></Field>
        <Field label={t("shop_ui_ends")} required><input type="datetime-local" value={draft.ends_at} onChange={(e)=>setDraft((x)=>({...x,ends_at:e.target.value}))}/></Field>
        <Field label={t("shop_ui_status")}><select value={draft.status} onChange={(e)=>setDraft((x)=>({...x,status:e.target.value}))}><option value="scheduled">{t("shop_ui_scheduled")}</option><option value="draft">{t("shop_ui_draft")}</option><option value="active">{t("shop_ui_active")}</option></select></Field>
      </div>
      <div className={styles.auctionCreateActions}><button type="button" className={styles.primaryButton} onClick={createLot}>{t("shop_ui_create_lot")}</button></div>
    </SettingsCard> : null}
    {auctionEnabled ? <SettingsCard title={t("shop_ui_auction_lots")} subtitle={t("shop_ui_manage_current_and_upcoming_auctions")}>
      {actionError ? <div className={styles.error}>{typeof actionError === "string" ? actionError : JSON.stringify(actionError)}</div> : null}
      {loadingLots ? <div className={styles.state}>{t("shop_ui_loading")}</div> : <div className={styles.auctionLotList}>{lots.length ? lots.map((lot)=><article key={lot.id} className={styles.auctionLotRow}>
        <div className={styles.auctionLotImage}>{lot.image_url ? <img src={lot.image_url} alt=""/> : <span>{t("shop_ui_lot")}</span>}</div>
        <div className={styles.auctionLotMain}><strong>{lot.title || lot.product_name}</strong><span>{lot.current_price} {currencySymbol} · {lot.bid_count} {t("shop_ui_bids")}</span><small>{new Date(lot.starts_at).toLocaleString()} → {new Date(lot.ends_at).toLocaleString()}</small></div>
        <span className={`${styles.auctionStatus} ${styles[`auctionStatus_${lot.status}`] || ""}`}>{lot.status}</span>
        <div className={styles.auctionLotActions}>{lot.status !== "active" && lot.status !== "ended" ? <button type="button" onClick={()=>setLotStatus(lot,"active")}>{t("shop_ui_start")}</button> : null}{lot.status === "active" ? <button type="button" onClick={()=>setLotStatus(lot,"paused")}>{t("shop_ui_pause")}</button> : null}{lot.status !== "cancelled" && lot.status !== "ended" ? <button type="button" onClick={()=>setLotStatus(lot,"cancelled")}>{t("shop_ui_cancel")}</button> : null}{!lot.bid_count ? <button type="button" className={styles.dangerTextButton} onClick={()=>deleteLot(lot)}>{t("shop_ui_delete")}</button> : null}</div>
      </article>) : <div className={styles.auctionEmpty}>{t("shop_ui_no_lots_yet_create_the_first_auction_from_a_crm_product")}</div>}</div>}
    </SettingsCard> : null}
  </div>;
}

function MarketplaceAdminPanel({ form, setForm, shopText, currencySymbol, categories = [] }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [businessDraft, setBusinessDraft] = useState({ title: "", description: "", price: "", price_negotiable: false, city: "", condition: "used", category_id: "", status: "published" });
  const [businessImages, setBusinessImages] = useState([]);
  const [businessBusy, setBusinessBusy] = useState(false);
  const marketplaceEnabled = Boolean(form.commerce_modules?.marketplace);
  const cfg = form.marketplace || {};
  const updateCfg = (key, value) => setForm((current) => ({ ...current, marketplace: { ...current.marketplace, [key]: value } }));
  const load = async () => {
    if (!marketplaceEnabled) return;
    setLoading(true); setError("");
    try {
      const [{ data }, reportResponse] = await Promise.all([
        api.get("/auth/marketplace/listings", { params: { status, limit: 300 } }),
        api.get("/auth/marketplace/reports", { params: { status: "open" } }).catch(() => ({ data: { items: [] } })),
      ]);
      setRows(Array.isArray(data?.items) ? data.items : []);
      setReports(Array.isArray(reportResponse?.data?.items) ? reportResponse.data.items : []);
    } catch (requestError) { setError(requestError?.response?.data?.detail || t("shop_ui_could_not_load_marketplace_listings")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [marketplaceEnabled, status]);
  const moderate = async (id, nextStatus) => { try { await api.patch(`/auth/marketplace/listings/${id}`, { status: nextStatus }); await load(); } catch (e) { setError(e?.response?.data?.detail || "Error"); } };
  const closeReport = async (id) => { await api.patch(`/auth/marketplace/reports/${id}`, { status: "closed" }); await load(); };
  const createBusinessListing = async () => {
    if (!businessDraft.title.trim() || businessDraft.description.trim().length < 10) return;
    setBusinessBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/marketplace/listings", {
        ...businessDraft,
        category_id: businessDraft.category_id ? Number(businessDraft.category_id) : null,
        price: businessDraft.price === "" ? null : Number(businessDraft.price),
      });
      for (const file of businessImages.slice(0, cfg.max_images || 8)) {
        const body = new FormData(); body.append("file", file);
        await api.post(`/auth/marketplace/listings/${data.id}/images`, body, { headers: { "Content-Type": "multipart/form-data" } });
      }
      setBusinessDraft({ title: "", description: "", price: "", price_negotiable: false, city: "", condition: "used", category_id: "", status: "published" });
      setBusinessImages([]); setStatus("all"); await load();
    } catch (e) { setError(e?.response?.data?.detail || t("shop_ui_could_not_create_listing")); }
    finally { setBusinessBusy(false); }
  };
  return <div className={styles.marketplaceAdminStack}>
    <SettingsCard title={t("shop_ui_business_listings")} subtitle={t("shop_ui_this_area_is_available_whenever_marketplace_is_enabled_listings_create")}>
      {!marketplaceEnabled ? <div className={styles.auctionDisabledNotice}>{t("shop_ui_marketplace_is_disabled_enable_it_in_common_settings")}</div> : null}
      <div className={styles.formGrid}>
        <ToggleCard label={t("shop_ui_allow_customers_to_post_listings")} hint={t("shop_ui_off_by_default_when_enabled_a_separate_tab_appears_with_customer_publi")} checked={cfg.user_listings?.enabled === true} onChange={(v) => setForm((current) => ({ ...current, marketplace: { ...current.marketplace, user_listings: { ...current.marketplace?.user_listings, enabled: v } } }))} />
        <ToggleCard label={t("shop_ui_allow_phone_on_business_listings")} hint={t("shop_ui_shared_phone_visibility_rule_for_public_listing_cards")} checked={cfg.allow_phone !== false} onChange={(v) => updateCfg("allow_phone", v)} />
        <Field label={t("shop_ui_business_listing_lifetime_days")}><input type="number" min="1" max="365" value={cfg.default_listing_days ?? 30} onChange={(e)=>updateCfg("default_listing_days", Number(e.target.value||30))}/></Field>
        <Field label={t("shop_ui_business_listing_photo_limit")}><input type="number" min="1" max="20" value={cfg.max_images ?? 8} onChange={(e)=>updateCfg("max_images", Number(e.target.value||8))}/></Field>
      </div>
    </SettingsCard>
    {marketplaceEnabled ? <SettingsCard title={t("shop_ui_create_a_business_listing")} subtitle={t("shop_ui_created_directly_from_crm_and_can_be_published_immediately")}>
      <div className={styles.formGrid}>
        <Field label={t("shop_ui_title")} required><input value={businessDraft.title} onChange={(e)=>setBusinessDraft((x)=>({...x,title:e.target.value}))}/></Field>
        <Field label={t("shop_ui_category")}><select value={businessDraft.category_id} onChange={(e)=>setBusinessDraft((x)=>({...x,category_id:e.target.value}))}><option value="">—</option>{categories.map((category)=><option key={category.id} value={category.id}>{category.name}</option>)}</select></Field>
        <Field label={t("shop_ui_city")}><input value={businessDraft.city} onChange={(e)=>setBusinessDraft((x)=>({...x,city:e.target.value}))}/></Field>
        <Field label={t("shop_ui_price_2")}><div className={styles.moneyInput}><span>{currencySymbol}</span><input type="number" min="0" step="0.01" value={businessDraft.price} disabled={businessDraft.price_negotiable} onChange={(e)=>setBusinessDraft((x)=>({...x,price:e.target.value}))}/></div></Field>
        <ToggleCard label={t("shop_ui_negotiable_price")} hint={t("shop_ui_the_listing_will_show_a_negotiable_price_label")} checked={businessDraft.price_negotiable} onChange={(v)=>setBusinessDraft((x)=>({...x,price_negotiable:v}))}/></div>
      <Field label={t("shop_ui_description")} className={styles.fullWidth}><textarea rows="5" value={businessDraft.description} onChange={(e)=>setBusinessDraft((x)=>({...x,description:e.target.value}))}/></Field>
      <div className={styles.marketplaceBusinessActions}><label className={styles.secondaryButton}>{t("shop_ui_choose_photos")}<input hidden type="file" accept="image/*" multiple onChange={(e)=>setBusinessImages(Array.from(e.target.files||[]).slice(0,cfg.max_images||8))}/></label><span>{businessImages.length ? businessImages.map((file)=>file.name).join(", ") : t("shop_ui_no_photos_selected")}</span><button type="button" className={styles.primaryButton} disabled={businessBusy || !businessDraft.title.trim() || businessDraft.description.trim().length < 10} onClick={createBusinessListing}>{businessBusy ? t("shop_ui_creating") : t("shop_ui_create_listing")}</button></div>
    </SettingsCard> : null}
    {marketplaceEnabled ? <SettingsCard title={t("shop_ui_listing_moderation")} subtitle={t("shop_ui_publish_reject_or_hide_user_listings")}>
      <div className={styles.marketplaceAdminToolbar}>{["pending","published","hidden","rejected","sold","all"].map(x=><button type="button" key={x} className={status===x?styles.filterButtonActive:""} onClick={()=>setStatus(x)}>{x}</button>)}</div>
      {error ? <div className={styles.error}>{typeof error === "string" ? error : JSON.stringify(error)}</div> : null}
      {loading ? <div className={styles.state}>{t("shop_ui_loading")}</div> : <div className={styles.marketplaceAdminList}>{rows.length ? rows.map(row=><article key={row.id} className={styles.marketplaceAdminRow}>
        <div><span className={styles.auctionStatus}>{row.status}</span><strong>{row.title}</strong><small>{row.seller_name} · {row.city || "—"} · {row.price} {currencySymbol}</small><p>{row.description}</p></div>
        <div className={styles.marketplaceAdminActions}>{row.status!=="published"?<button type="button" onClick={()=>moderate(row.id,"published")}>{t("shop_ui_publish")}</button>:null}{row.status!=="hidden"?<button type="button" onClick={()=>moderate(row.id,"hidden")}>{t("shop_ui_hide")}</button>:null}{row.status!=="rejected"?<button type="button" className={styles.dangerTextButton} onClick={()=>moderate(row.id,"rejected")}>{t("shop_ui_reject")}</button>:null}</div>
      </article>) : <div className={styles.auctionEmpty}>{t("shop_ui_no_listings_with_this_status")}</div>}</div>}
    </SettingsCard> : null}
    {marketplaceEnabled && reports.length ? <SettingsCard title={t("shop_ui_reports")} subtitle={t("shop_ui_open_user_reports_about_listings")}><div className={styles.marketplaceReportList}>{reports.map(row=><article key={row.id}><div><strong>#{row.listing_id} · {row.reason}</strong><p>{row.details || "—"}</p></div><button type="button" onClick={()=>closeReport(row.id)}>{t("shop_ui_close")}</button></article>)}</div></SettingsCard> : null}
  </div>;
}


function MarketplaceUserListingsPanel({ form, setForm, shopText }) {
  const { t } = useTranslation();
  const cfg = form.marketplace?.user_listings || EMPTY_FORM.marketplace.user_listings;
  const update = (key, value) => setForm((current) => ({
    ...current,
    marketplace: {
      ...current.marketplace,
      user_listings: { ...current.marketplace?.user_listings, [key]: value },
    },
  }));
  return <div className={styles.userListingsStack}>
    <SettingsCard title={t("shop_ui_customer_listing_publishing")} subtitle={t("shop_ui_limits_apply_only_to_registered_customers_crm_staff_listings_are_not_c")}>
      <div className={styles.userPublishingStatus}>
        <div><strong>{t("shop_ui_customer_listings_enabled")}</strong><span>{t("shop_ui_retailshop_shows_post_listing_and_my_listings")}</span></div>
        <button type="button" className={styles.secondaryButton} onClick={()=>update("enabled", false)}>{t("shop_ui_disable")}</button>
      </div>
    </SettingsCard>
    <SettingsCard title={t("shop_ui_limits")} subtitle={t("shop_ui_protect_the_marketplace_from_spam_and_leave_room_for_paid_publishing_l")}>
      <div className={styles.formGrid}>
        <Field label={t("shop_ui_free_listings_per_week")}><input type="number" min="0" max="100" value={cfg.free_per_week ?? 3} onChange={(e)=>update("free_per_week", Number(e.target.value||0))}/></Field>
        <Field label={t("shop_ui_maximum_active_listings")}><input type="number" min="1" max="500" value={cfg.max_active ?? 10} onChange={(e)=>update("max_active", Number(e.target.value||1))}/></Field>
        <Field label={t("shop_ui_photos_per_listing")}><input type="number" min="1" max="20" value={cfg.max_photos ?? 8} onChange={(e)=>update("max_photos", Number(e.target.value||1))}/></Field>
        <Field label={t("shop_ui_listing_lifetime_days")}><input type="number" min="1" max="365" value={cfg.lifetime_days ?? 30} onChange={(e)=>update("lifetime_days", Number(e.target.value||30))}/></Field>
      </div>
    </SettingsCard>
    <SettingsCard title={t("shop_ui_moderation_and_safety")} subtitle={t("shop_ui_recommended_defaults_are_enabled")}>
      <div className={styles.settingsToggleGrid}>
        <ToggleCard label={t("shop_ui_moderate_new_listings")} hint={t("shop_ui_new_listings_start_in_pending_status")} checked={cfg.moderation_required !== false} onChange={(v)=>update("moderation_required",v)}/>
        <ToggleCard label={t("shop_ui_re_moderate_after_edits")} hint={t("shop_ui_published_listings_return_to_moderation_after_important_edits")} checked={cfg.remoderate_after_edit !== false} onChange={(v)=>update("remoderate_after_edit",v)}/>
        <ToggleCard label={t("shop_ui_internal_chat")} hint={t("shop_ui_buyer_and_seller_communicate_inside_retailshop")} checked={cfg.internal_chat !== false} onChange={(v)=>update("internal_chat",v)}/>
        <ToggleCard label={t("shop_ui_allow_seller_phone")} hint={t("shop_ui_the_seller_still_decides_whether_to_display_the_number")} checked={cfg.allow_phone !== false} onChange={(v)=>update("allow_phone",v)}/>
      </div>
    </SettingsCard>
    <SettingsCard title={t("shop_ui_listing_rules")} subtitle={t("shop_ui_control_which_fields_customers_must_complete")}>
      <div className={styles.settingsToggleGrid}>
        <ToggleCard label={t("shop_ui_city_is_required")} hint={t("shop_ui_a_listing_cannot_be_submitted_without_a_city")} checked={cfg.city_required !== false} onChange={(v)=>update("city_required",v)}/>
        <ToggleCard label={t("shop_ui_price_is_required")} hint={t("shop_ui_except_when_negotiable_pricing_is_allowed_and_selected")} checked={cfg.price_required !== false} onChange={(v)=>update("price_required",v)}/>
        <ToggleCard label={t("shop_ui_allow_negotiable_price")} hint={t("shop_ui_customers_may_post_without_a_fixed_amount")} checked={cfg.negotiable_price !== false} onChange={(v)=>update("negotiable_price",v)}/>
        <ToggleCard label={t("shop_ui_allow_services")} hint={t("shop_ui_the_customer_form_includes_the_service_type")} checked={cfg.allow_services !== false} onChange={(v)=>update("allow_services",v)}/>
      </div>
    </SettingsCard>
    <SettingsCard title={t("shop_ui_customer_experience")} subtitle={t("shop_ui_before_the_first_listing_customers_accept_marketplace_rules_the_ui_sho")}>
      <div className={styles.userListingPreview}>
        <div><span>{t("shop_ui_weekly_allowance")}</span><strong>{cfg.free_per_week ?? 3}</strong><small>{t("shop_ui_free_listings")}</small></div>
        <div><span>{t("shop_ui_active_at_once")}</span><strong>{cfg.max_active ?? 10}</strong><small>{t("shop_ui_maximum")}</small></div>
        <div><span>{t("shop_ui_photos")}</span><strong>{cfg.max_photos ?? 8}</strong><small>{t("shop_ui_per_listing")}</small></div>
        <div><span>{t("shop_ui_lifetime")}</span><strong>{cfg.lifetime_days ?? 30}</strong><small>{t("shop_ui_days")}</small></div>
      </div>
    </SettingsCard>
  </div>;
}

function SettingsCard({ title, subtitle, children }) {
  return <div className={styles.card}><div className={styles.cardHead}><div><h2>{title}</h2>{subtitle ? <p>{subtitle}</p> : null}</div></div>{children}</div>;
}

function ToggleCard({ label, hint, checked, onChange }) {
  return <label className={`${styles.toggleCard} ${styles.fullWidth}`}><span><strong>{label}</strong><small>{hint}</small></span><input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} /></label>;
}

function Field({ label, hint, required = false, className = "", children }) {
  return <label className={`${styles.field} ${className}`}><span>{label}{required ? " *" : ""}</span>{children}{hint ? <small>{hint}</small> : null}</label>;
}

function TextAreaField({ label, value, onChange }) {
  return <Field label={label} className={styles.fullWidth}><textarea rows="6" value={value} onChange={(e) => onChange(e.target.value)} /></Field>;
}

function ColorField({ label, value, onChange, disabled = false }) {
  return <label className={`${styles.colorField} ${disabled ? styles.colorFieldDisabled : ""}`}><span>{label}</span><div><input type="color" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} /><input value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} /></div></label>;
}

function AssetPreview({ title, url, fallback, small = false }) {
  return <div className={styles.assetPreview}><div className={small ? styles.assetImageSmall : styles.assetImage}>{url ? <img src={url} alt="" /> : <span>{fallback}</span>}</div><div><strong>{title}</strong><small>{url ? url.split("/").pop() : "—"}</small></div></div>;
}

function UploadRow({ title, hint, accept, url, busy, onPick, onRemove, t }) {
  const inputRef = useRef(null);
  return <div className={styles.uploadRow}><div className={styles.uploadInfo}><strong>{title}</strong><small>{hint}</small></div><div className={styles.uploadActions}><input ref={inputRef} type="file" accept={accept} hidden onChange={(e) => { onPick(e.target.files?.[0]); e.target.value = ""; }} /><button type="button" disabled={busy} onClick={() => inputRef.current?.click()}>{busy ? t("shop_settings_asset_loading") : t("shop_settings_asset_upload")}</button><button type="button" className={styles.removeButton} disabled={busy || !url} onClick={onRemove}>{t("shop_settings_asset_remove")}</button></div>{url ? <div className={styles.uploadCurrent}><img src={url} alt="" /><span>{url.split("/").pop()}</span></div> : null}</div>;
}
